import {
  forwardRef,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Popover, Tooltip } from 'antd';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import maxBy from 'lodash/maxBy';
import { isUndefined } from 'lodash';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  coverageFormatter,
  currencyFormatter,
  findFrequencyByBenefitKind,
  getAdditionalServices,
  getConfiguredBenefitCodeList,
  getEEContributionByFrequency,
  getEEContributionField,
  getNotApplicableTitles,
  hideHeader,
  showRxRow,
} from 'modules/benefitsWellness/util';
import useBenguide from 'hooks/benguide';
import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import meta from 'modules/benefitsWellness/data/metadataNew';
import MDVPlan from 'model/MDVPlan';
import CustomField from 'model/CustomField';
import HSAMedicalContribution from 'model/HSAMedicalContribution';
import MDVTypes from 'modules/benefitsWellness/enums/MDVTypes';
import MDVMetaGroup from 'model/MDVMetaGroup';
import iconEdit from 'images/icon-edit.svg';
import PrescriptionPill from 'images/icons/ai-prescription-pill.svg';
import iconPlayPlanInfo from 'images/icon-play-plan-info.svg';
import iconDocSearch from 'images/icon-doc-search-plans.svg';
import iconLeftArrow from 'images/icons/Icon-left-arrow.svg';
import iconRightArrow from 'images/icons/Icon-right-arrow.svg';
import useUPClient from 'modules/clients/UPClient/useUPClient';
import {
  DENTAL_PLAN_CHANNEL,
  MEDICAL_PLAN_CHANNEL,
  VISION_PLAN_CHANNEL,
} from 'modules/clients/UPClient/UPClientConst';
import {
  AGE_BAND,
  DENTAL_SERVICES_LIST,
  FOUR_TIER,
  IN_NETWORK,
  MAIL_ORDER_RX_TIER_LABELS,
  MEDICAL_SERVICES_LIST,
  MOBILE_DEDUCTIBLE_LABELS,
  MOBILE_DENTAL_DEDUCTIBLE_LABELS,
  MOBILE_IND_DEDUCTIBLE_LABELS,
  MOBILE_RX_DEDUCTIBLE_OOP_LABELS,
  NDASH,
  NETWORK_PREFIX,
  RX_TIER_LABELS,
  VISION_FREQUENCY_LIST,
  VISION_SERVICES_LIST,
} from 'modules/benefitsWellness/constants/BenefitConstants';
import MDVMetaService from 'model/MDVMetaService';
import StyledCheckbox from 'components/StyledCheckbox/StyledCheckbox';
import { setSelectedPlanForCompare } from 'modules/benefitsWellness/slices/benefitWellnessSlice';
import {
  editCustomFieldValue,
  getCustomFieldsToState,
} from 'modules/benefitsWellness/slices/medicalSlice';
import {
  editDentalCustomFieldValue,
  getDentalCustomFieldsToState,
} from 'modules/benefitsWellness/slices/dentalSlice';
import {
  editVisionCustomFieldValue,
  getVisionCustomFieldsToState,
} from 'modules/benefitsWellness/slices/visionSlice';
import MissingInfo from 'modules/benefitsWellness/components/MissingInfo/MissingInfo';
import MDVFormattedValue from 'modules/benefitsWellness/components/MDVFormattedValue/MDVFormattedValue';
import ProviderSearchModal from 'modules/benefitsWellness/components/ProviderSearchModal/ProviderSearchModal';
import PrescriptionDrugListModal from 'modules/benefitsWellness/components/PrescriptionDrugListModal/PrescriptionDrugListModal';
import PlanTypeInfoDrawer from 'modules/benefitsWellness/components/PlanTypeInfoDrawer/PlanTypeInfoDrawer';
import {
  TableContext,
  useTableHeightSync,
} from 'modules/benefitsWellness/components/TableContext/TableContextProvider';
import { TaxAdvantageBenefitKind } from 'modules/taxAdvantaged/constants/taxAdvantagedConstants';
import {
  DOUBLE_DASH,
  NO_DATA,
  NOT_APPLICABLE_FORMATTED,
  NOT_COVERED_FORMATTED,
  TRIPLE_DASH,
} from 'utils/planRecommenderUtils/planRecommenderUtils';
import {
  getLabelsSuperscriptWithKey,
  getSupTextFromReactNode,
} from 'utils/labelUtils';
import {
  DENTAL_FAMILY_DEDUCTIBLE_IN,
  DENTAL_FAMILY_DEDUCTIBLE_OUT,
  DENTAL_INDIVIDUAL_DEDUCTIBLE_IN,
  DENTAL_INDIVIDUAL_DEDUCTIBLE_OUT,
  DENTAL_ORTHODONTIA_MAX_IN,
  DENTAL_ORTHODONTIA_MAX_OUT,
  DENTAL_PLAN_YEAR_MAX_IN,
  DENTAL_PLAN_YEAR_MAX_OUT,
  MEDICAL_FAMILY_DEDUCTIBLE_IN,
  MEDICAL_FAMILY_DEDUCTIBLE_OUT,
  MEDICAL_FAMILY_OOP_MAX_IN,
  MEDICAL_FAMILY_OOP_MAX_OUT,
  MEDICAL_INDIVIDUAL_DEDUCTIBLE_IN,
  MEDICAL_INDIVIDUAL_DEDUCTIBLE_OUT,
  MEDICAL_INDIVIDUAL_OOP_MAX_IN,
  MEDICAL_INDIVIDUAL_OOP_MAX_OUT,
  MEDICAL_INDIVIDUAL_WITHIN_FAMILY_DEDUCTIBLE_IN,
  MEDICAL_INDIVIDUAL_WITHIN_FAMILY_DEDUCTIBLE_OUT,
  MEDICAL_INDIVIDUAL_WITHIN_FAMILY_OOP_MAX_IN,
  MEDICAL_INDIVIDUAL_WITHIN_FAMILY_OOP_MAX_OUT,
  MEDICAL_RX_FAMILY_DEDUCTIBLE_IN,
  MEDICAL_RX_FAMILY_DEDUCTIBLE_OUT,
  MEDICAL_RX_FAMILY_OOP_MAX_IN,
  MEDICAL_RX_FAMILY_OOP_MAX_OUT,
  MEDICAL_RX_INDIVIDUAL_DEDUCTIBLE_IN,
  MEDICAL_RX_INDIVIDUAL_DEDUCTIBLE_OUT,
  MEDICAL_RX_INDIVIDUAL_OOP_MAX_IN,
  MEDICAL_RX_INDIVIDUAL_OOP_MAX_OUT,
} from 'modules/benefitsWellness/constants/labelConstants';
import {
  BENEFIT_TYPES,
  MEDICAL_ADVANCED_PROFILE_PARAMETERS,
  VISION_ADVANCED_PROFILE_PARAMETERS,
} from 'constants/commonConstants';
import { FORMATTED_FREQUENCY } from 'modules/benefitsWellness/constants/TooltipConstants';
import EditCustomRowModal from 'components/EditCustomRowModal/EditCustomRowModal';
import MDVCardCustomValue from 'modules/benefitsWellness/components/MDVCardCustomValue/MDVCardCustomValue';
import {
  PROVIDER_SEARCH,
  PLAN_NETWORK,
  INFO,
  FREQUENCIES_TEXT,
  SERVICES_TEXT,
  COMPARE,
  COVERAGE_JUST_YOU,
  COVERAGE_YOU_SPOUSE,
  COVERAGE_YOU_CHILDREN,
  COVERAGE_YOU_FAMILY,
  COVERAGE_YOU_SPOUSE_OR_CHILDREN,
  RX,
  RX_TIER_FIELD_KEYS,
  DEDUCTIBLES_AND_OOP_MAXIMUM,
  DEDUCTIBLES_BENEFIT_MAXIMUMS,
  SPECIALIZED_FIELD_KEYS,
  FIELD_KEYS,
  YOUR_COST,
  COST as COST_TEXT,
  FREQUENCY_KEYS,
} from 'constants/languageConstants';
import { getText } from 'utils/stringUtil';
import styles from './mdvCardValues.module.less';

const IN = 'In';
const OUT_OF = 'OutOf';
const COST = 'Cost';
const APPLICABLE = 'Applicable';
const DEDUCTIBLE = 'deductibles';
const IND_WITHIN_FAMILY_DEDUCTIBLE = 'indWithinFamilyDeductible';
const OUT_OF_POCKET = 'outOfPocket';
const IND_WITHIN_FAMILY_OOP_MAX = 'indWithinFamilyOOPMax';
const MONTHLY_COST = 'monthlyCost';
const CUSTOM_FIELD = 'customFields';
const DEDUCTIBLE_COST = 'deductibleCost';
const OUT_OF_POCKET_MAX = 'outOfPocketMax';
const PLAN_YEAR_MAX = 'calendarYearMax';
const ORTHODONTIA_MAX = 'orthodontiaLifetimeMax';
const RX_DEDUCTIBLES = 'rxDeductibles';
const RX_DEDUCTIBLE_AND_OOP_ATTRIBUTE_NAME = 'rxDeductiblesAndOop';
const RX_OOP_MAX = 'rxOOP';
const defaultValue = '-';
const IS_INDIVIDUAL = 'individual';

type SuperScriptMapDeductibles = {
  [RX_DEDUCTIBLES]: JSX.Element;
  [RX_DEDUCTIBLE_AND_OOP_ATTRIBUTE_NAME]: JSX.Element;
  [RX_OOP_MAX]: JSX.Element;
};

type SuperScriptMapIndFamilyWithin = {
  [IND_WITHIN_FAMILY_OOP_MAX]: JSX.Element;
  [IND_WITHIN_FAMILY_DEDUCTIBLE]: JSX.Element;
};

interface DeductibleDetails {
  individual: JSX.Element;
  family?: JSX.Element;
}

type SuperScriptMapper = {
  [key: string]: {
    [key in MDVTypes]?: DeductibleDetails;
  };
};

type MDVCardValueProps = {
  key: number;
  plan: any;
  planType: MDVTypes;
  moveLeft: () => void;
  moveRight: () => void;
  hideLeftMoveButton: boolean;
  hideRightMoveButton: boolean;
  summarySection?: ReactNode;
  attachedPlans: MDVPlan[];
  detailsHeight?: number;
  onHigherDetailsHeight?: (height: number) => void;
  isChanged?: Function;
  planIds: string[];
  dataIndex?: number;
};

export type MDVCardValuesRef = {
  [key: string]: number | undefined;
};

const MDVCardValues = forwardRef<MDVCardValuesRef, MDVCardValueProps>(
  (props, ref) => {
    const {
      plan,
      planType,
      hideLeftMoveButton,
      hideRightMoveButton,
      moveLeft,
      moveRight,
      summarySection,
      attachedPlans,
      detailsHeight,
      onHigherDetailsHeight,
      isChanged,
      planIds,
      dataIndex,
    } = props;
    const currentBenguide = useBenguide();
    const dispatch = useAppDispatch();
    const { rateValidations, rateValidationInProgress } = useAppSelector(
      (state) => state.benefitWellness
    );
    const { selectedNetwork, selectedPlanForCompare, selectedCoverageType } =
      useAppSelector((state) => state.benefitWellness);
    const { isMobile } = useAppSelector((state) => state.layout);
    const { data: hraPlanList } = useAppSelector((state) => state.hra.planList);
    const language = useAppSelector((state) => state.benguide.data?.language);
    const isEditMode = currentBenguide.isEditMode;

    const [dataGroups, setDataGroups] = useState<MDVMetaGroup[]>([]);
    const [isOpenProviderSearch, setIsOpenProviderSearch] =
      useState<boolean>(false);
    const [isOpenPrescriptionDrugList, setIsOpenPrescriptionDrugList] =
      useState<boolean>(false);
    const [additionalServices, setAdditionalServices] = useState<
      MDVMetaService[]
    >([]);
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const filteredServicesRef = useRef<any[]>([]);
    filteredServicesRef.current = [];
    const planNetworkRef = useRef<HTMLDivElement>(null);
    const detailsSectionRef = useRef<HTMLDivElement>(null);
    const [notApplicableTitles, setNotApplicableTitles] = useState<string[]>(
      []
    );
    const frequencyServicesRef = useRef<any[]>([]);
    const { getMaxHeight } = useContext(TableContext);

    // Row counter for preview mode - only count content rows
    const contentRowCounterRef = useRef(0);

    // Reset row counter when component re-renders
    useEffect(() => {
      contentRowCounterRef.current = 0;
    });

    // Function to get row wrapper class with correct background color
    const getRowWrapperClass = (hasActualContent: boolean = false) => {
      if (isEditMode) {
        // In edit mode, use default CSS nth-child behavior
        return styles.rowWrapper;
      }

      // In preview mode, only apply alternating colors to rows with actual content
      if (hasActualContent) {
        // For rows with actual content, manually calculate odd/even based on content row count
        const currentIndex = contentRowCounterRef.current++;
        const isEven = (currentIndex + 1) % 2 === 0;

        return `${styles.rowWrapper} ${
          isEven ? styles.evenRow : styles.oddRow
        }`;
      } else {
        // For separators, empty rows, etc., just return the base class without counting
        return styles.rowWrapper;
      }
    };
    const { height: planNameHeight, ref: planNameHeightRef } =
      useTableHeightSync('Plan Name', dataIndex);
    const { height: planNetworkHeight, ref: planNetworkHeightRef } =
      useTableHeightSync('Plan Network', dataIndex);
    const { height: monthlyCostHeight, ref: monthlyCostHeightRef } =
      useTableHeightSync('Monthly Cost', dataIndex);
    const {
      height: deductiblesIndividualHeight,
      ref: deductiblesIndividualHeightRef,
    } = useTableHeightSync('Individual Deductible', dataIndex);
    const { height: deductiblesFamilyHeight, ref: deductiblesFamilyHeightRef } =
      useTableHeightSync('Family Deductible', dataIndex);
    const { height: deductibleWithinHeight, ref: deductibleWithinHeightRef } =
      useTableHeightSync('Deductible (Ind within Fam)', dataIndex);
    const { height: oopMaxIndividualHeight, ref: oopMaxIndividualHeightRef } =
      useTableHeightSync('Individual Out-of-Pocket Max', dataIndex);
    const { height: oopMaxFamilyHeight, ref: oopMaxFamilyHeightRef } =
      useTableHeightSync('Family Out-of-Pocket Max', dataIndex);
    const { height: oopWithinHeight, ref: oopWithinHeightRef } =
      useTableHeightSync('Out-of-Pocket Max (Ind within Fam)', dataIndex);
    const {
      height: planYearMaxIndividualHeight,
      ref: planYearMaxIndividualHeightRef,
    } = useTableHeightSync('Annual Maximum (Per Person)', dataIndex);
    const { height: planYearMaxFamilyHeight, ref: planYearMaxFamilyHeightRef } =
      useTableHeightSync('Annual Maximum (Per Person)', dataIndex);
    const {
      height: orthodontiaMaxIndividualHeight,
      ref: orthodontiaMaxIndividualHeightRef,
    } = useTableHeightSync('Orthodontia Maximum', dataIndex);
    const {
      height: orthodontiaMaxFamilyHeight,
      ref: orthodontiaMaxFamilyHeightRef,
    } = useTableHeightSync('Orthodontia Maximum', dataIndex);
    const { height: rxDeductiblesIndividualHeight } = useTableHeightSync(
      'Rx Individual Deductible',
      dataIndex
    );
    const { height: rxDeductiblesFamilyHeight } = useTableHeightSync(
      'Rx Family Deductible',
      dataIndex
    );
    const { height: rxOopMaxIndividualHeight } = useTableHeightSync(
      'Rx Individual Out-of-Pocket Max',
      dataIndex
    );
    const { height: rxOopMaxFamilyHeight } = useTableHeightSync(
      'Rx Family Out-of-Pocket Max',
      dataIndex
    );
    const { height: providerSearchHeight, ref: providerSearchHeightRef } =
      useTableHeightSync('Provider Search', dataIndex);
    const { height: planTypeInfoHeight, ref: planTypeInfoHeightRef } =
      useTableHeightSync('Plan Type Info', dataIndex);
    const { height: prescriptionDrugListHeight, ref: prescriptionDrugListRef } =
      useTableHeightSync('Prescription Drug List', dataIndex);

    useImperativeHandle(ref, () => ({
      planNameHeight,
      planNetworkHeight,
      monthlyCostHeight,
      deductiblesIndividualHeight,
      deductiblesFamilyHeight,
      deductibleWithinHeight,
      oopMaxIndividualHeight,
      oopMaxFamilyHeight,
      oopWithinHeight,
      planYearMaxIndividualHeight,
      planYearMaxFamilyHeight,
      orthodontiaMaxIndividualHeight,
      orthodontiaMaxFamilyHeight,
      rxDeductiblesIndividualHeight,
      rxDeductiblesFamilyHeight,
      rxOopMaxIndividualHeight,
      rxOopMaxFamilyHeight,
      providerSearchHeight,
      planTypeInfoHeight,
      prescriptionDrugListHeight,
    }));

    useLayoutEffect(() => {
      if (
        detailsSectionRef.current?.scrollHeight &&
        detailsHeight !== undefined &&
        onHigherDetailsHeight
      ) {
        if (detailsHeight < detailsSectionRef.current.scrollHeight) {
          onHigherDetailsHeight(detailsSectionRef.current?.scrollHeight);
        }
      }
    }, [onHigherDetailsHeight, detailsHeight]);

    useEffect(() => {
      isChanged && isChanged((i: any) => !i);
      if (planType) {
        const fieldGroups = meta.benguideMetaData.planFieldGroupings.find(
          (planFieldGroup) => planFieldGroup.planType === planType.toLowerCase()
        );
        const definedBenefitCodes = getConfiguredBenefitCodeList(
          fieldGroups?.hotspots || []
        );
        setAdditionalServices(
          getAdditionalServices(
            attachedPlans,
            definedBenefitCodes,
            selectedNetwork
          )
        );
        setDataGroups(fieldGroups?.hotspots || []);
      }
    }, [
      planType,
      attachedPlans,
      dispatch,
      currentBenguide.masterId,
      isChanged,
      selectedNetwork,
    ]);

    const computedTitles = useMemo(() => {
      if (isEditMode) return [];

      return getNotApplicableTitles(
        attachedPlans,
        planType,
        selectedNetwork === 'IN_NETWORK' ? 'IN' : 'OUT'
      );
    }, [isEditMode, attachedPlans, planType, selectedNetwork]);

    useEffect(() => {
      setNotApplicableTitles(computedTitles);
    }, [computedTitles]);

    const benguideData = useAppSelector((state) => state.benguide);
    const { urlHash } = benguideData.data;

    const benguide = useBenguide();
    const navigate = useNavigate();

    const {
      name,
      customServices = [],
      rxCosts = {},
      benefitCarrier,
      mailOrderRxCosts = {},
      hsaMedicalContributionVO: _hsaMedicalContributionVO,
    } = plan || {};

    const hsaPlanIds =
      useAppSelector((state) => state.hsa.hsaSection)?.plans?.map(
        (plan) => plan.planId
      ) || [];

    const hraPlanIds =
      useAppSelector((state) => state.hra.hraSection)?.plans?.map(
        (plan) => plan.planId
      ) || [];

    const hsaMedicalContributionVO: HSAMedicalContribution[] =
      _hsaMedicalContributionVO
        ? _hsaMedicalContributionVO?.filter(
            (entry: HSAMedicalContribution) =>
              hsaPlanIds.includes(entry.planId) && plan.hsaCompatible
          )
        : [];

    const hraMedicalContributionVO: HSAMedicalContribution[] =
      _hsaMedicalContributionVO
        ? _hsaMedicalContributionVO?.filter((entry: HSAMedicalContribution) =>
            hraPlanIds.includes(entry.planId)
          )
        : [];

    const networkTextPrefix =
      selectedNetwork === IN_NETWORK.value ? IN : OUT_OF;
    const networkType =
      selectedNetwork === IN_NETWORK.value
        ? NETWORK_PREFIX.IN
        : NETWORK_PREFIX.OUT_OF;

    const showIfExistServiceList = dataGroups
      .filter((titleGroup) => !!titleGroup.isCustomizedServices)
      .map((titleGroup) =>
        titleGroup.elements.filter((title: any) => title.showTitleIfExists)
      )[0] as MDVMetaService[];

    const showIfExistRxCostsList = dataGroups
      .filter((titleGroup) => !!titleGroup.isCustomizedRX)
      .map((titleGroup) =>
        titleGroup.elements.filter((title: any) => title.showTitleIfExists)
      )[0] as MDVMetaService[];

    const showIfExistDeductibleIndividualList = dataGroups
      ?.filter((item) => item?.isCustomizedDeductible)
      ?.flatMap((item) => item?.elements) as MDVMetaService[];

    const nonExistCustomServices = {} as any;
    const nonExistRxCosts = {} as any;
    const nonExistDeductibleIndividual = {} as any;
    attachedPlans.forEach((plan) => {
      showIfExistServiceList &&
        showIfExistServiceList.forEach((service) => {
          if (
            plan.customServices &&
            !plan.customServices.find(
              (customService) =>
                customService.benefitCode &&
                customService.benefitCode.code === service.serviceBenefitCode
            )
          ) {
            nonExistCustomServices[service.serviceBenefitCode] =
              nonExistCustomServices[service.serviceBenefitCode]
                ? nonExistCustomServices[service.serviceBenefitCode] + 1
                : 1;
          }
        });

      showIfExistRxCostsList &&
        showIfExistRxCostsList.forEach((rxCost) => {
          if (
            (rxCost.fieldName === 'rxCost' &&
              rxCost.rxCode &&
              plan.rxCosts &&
              !(
                plan.rxCosts['inNetwork'].hasOwnProperty(rxCost.rxCode) ||
                plan.rxCosts['outOfNetwork'].hasOwnProperty(rxCost.rxCode)
              )) ||
            (rxCost.fieldName === 'mailRxCost' &&
              rxCost.rxCode &&
              plan.mailOrderRxCosts &&
              !(
                plan.mailOrderRxCosts['inNetwork'].hasOwnProperty(
                  rxCost.rxCode
                ) ||
                plan.mailOrderRxCosts['outOfNetwork'].hasOwnProperty(
                  rxCost.rxCode
                )
              ) &&
              nonExistRxCosts)
          ) {
            nonExistRxCosts[rxCost.fieldName + rxCost.rxCode] = nonExistRxCosts[
              rxCost.fieldName + rxCost.rxCode
            ]
              ? nonExistRxCosts[rxCost.fieldName + rxCost.rxCode] + 1
              : 1;
          }
        });
      showIfExistDeductibleIndividualList?.forEach((deductibleIndividual) => {
        if (
          deductibleIndividual.fieldName === IND_WITHIN_FAMILY_OOP_MAX &&
          (plan?.deductibles?.hasOwnProperty(
            'individualWithinFamilyInNetworkCost'
          ) ||
            plan?.deductibles?.hasOwnProperty(
              'individualWithinFamilyOutOfNetworkCost'
            ))
        ) {
          nonExistDeductibleIndividual[deductibleIndividual.fieldName] =
            nonExistDeductibleIndividual[deductibleIndividual.fieldName]
              ? nonExistDeductibleIndividual[deductibleIndividual.fieldName] + 1
              : 1;
        }
        if (
          deductibleIndividual.fieldName === IND_WITHIN_FAMILY_DEDUCTIBLE &&
          (plan?.deductibles?.hasOwnProperty(
            'individualWithinFamilyInNetworkCost'
          ) ||
            plan?.deductibles?.hasOwnProperty(
              'individualWithinFamilyOutOfNetworkCost'
            ))
        ) {
          nonExistDeductibleIndividual[deductibleIndividual.fieldName] =
            nonExistDeductibleIndividual[deductibleIndividual?.fieldName]
              ? nonExistDeductibleIndividual[deductibleIndividual?.fieldName] +
                1
              : 1;
        }
      });
    });

    const showCoverageDetails = (
      type: string,
      specialization: 'individual' | 'family',
      ref?: any,
      height?: number
    ) => {
      // This covers deductible and oop individual and out of pocket
      let individualValue = coverageFormatter(
        getCoverageValue(type, true, COST),
        getCoverageValue(type, true, APPLICABLE)
      );
      let familyValue = coverageFormatter(
        getCoverageValue(type, false, COST),
        getCoverageValue(type, false, APPLICABLE)
      );
      if (
        type === DEDUCTIBLE ||
        type === OUT_OF_POCKET ||
        type === PLAN_YEAR_MAX ||
        type === ORTHODONTIA_MAX ||
        type === IND_WITHIN_FAMILY_DEDUCTIBLE
      ) {
        if (
          individualValue === NOT_APPLICABLE_FORMATTED ||
          individualValue === DOUBLE_DASH ||
          individualValue === TRIPLE_DASH ||
          individualValue === NO_DATA
        ) {
          individualValue = defaultValue;
        }
        if (
          familyValue === NOT_APPLICABLE_FORMATTED ||
          familyValue === DOUBLE_DASH ||
          familyValue === TRIPLE_DASH ||
          familyValue === NO_DATA
        ) {
          familyValue = defaultValue;
        }
      }

      const network = networkType === NETWORK_PREFIX.IN ? 'IN' : 'OUT';

      const labelParams = plan?.planLabels?.parameters ?? [];
      const getSuperscript = (key: string) => {
        return (
          <sup>
            {getLabelsSuperscriptWithKey(
              labelParams,
              key,
              network!
            )?.toString() || ''}
          </sup>
        );
      };

      const superScriptMapper: SuperScriptMapper = {
        [DEDUCTIBLE]: {
          [MDVTypes.MEDICAL]: {
            individual: getSuperscript(
              network === 'IN'
                ? MEDICAL_INDIVIDUAL_DEDUCTIBLE_IN
                : MEDICAL_INDIVIDUAL_DEDUCTIBLE_OUT
            ),
            family: getSuperscript(
              network === 'IN'
                ? MEDICAL_FAMILY_DEDUCTIBLE_IN
                : MEDICAL_FAMILY_DEDUCTIBLE_OUT
            ),
          },
          [MDVTypes.DENTAL]: {
            individual: getSuperscript(
              network === 'IN'
                ? DENTAL_INDIVIDUAL_DEDUCTIBLE_IN
                : DENTAL_INDIVIDUAL_DEDUCTIBLE_OUT
            ),
            family: getSuperscript(
              network === 'IN'
                ? DENTAL_FAMILY_DEDUCTIBLE_IN
                : DENTAL_FAMILY_DEDUCTIBLE_OUT
            ),
          },
        },
        [OUT_OF_POCKET]: {
          [MDVTypes.MEDICAL]: {
            individual: getSuperscript(
              network === 'IN'
                ? MEDICAL_INDIVIDUAL_OOP_MAX_IN
                : MEDICAL_INDIVIDUAL_OOP_MAX_OUT
            ),
            family: getSuperscript(
              network === 'IN'
                ? MEDICAL_FAMILY_OOP_MAX_IN
                : MEDICAL_FAMILY_OOP_MAX_OUT
            ),
          },
        },
        [IND_WITHIN_FAMILY_OOP_MAX]: {
          [MDVTypes.MEDICAL]: {
            individual: getSuperscript(
              network === 'IN'
                ? MEDICAL_INDIVIDUAL_WITHIN_FAMILY_OOP_MAX_IN
                : MEDICAL_INDIVIDUAL_WITHIN_FAMILY_OOP_MAX_OUT
            ),
          },
        },
        [IND_WITHIN_FAMILY_DEDUCTIBLE]: {
          [MDVTypes.MEDICAL]: {
            individual: getSuperscript(
              network === 'IN'
                ? MEDICAL_INDIVIDUAL_WITHIN_FAMILY_DEDUCTIBLE_IN
                : MEDICAL_INDIVIDUAL_WITHIN_FAMILY_DEDUCTIBLE_OUT
            ),
          },
        },
        [PLAN_YEAR_MAX]: {
          [MDVTypes.DENTAL]: {
            individual: getSuperscript(
              network === 'IN'
                ? DENTAL_PLAN_YEAR_MAX_IN
                : DENTAL_PLAN_YEAR_MAX_OUT
            ),
          },
        },
        [ORTHODONTIA_MAX]: {
          [MDVTypes.DENTAL]: {
            individual: getSuperscript(
              network === 'IN'
                ? DENTAL_ORTHODONTIA_MAX_IN
                : DENTAL_ORTHODONTIA_MAX_OUT
            ),
          },
        },
      };

      const individualDeductibleSuperScript =
        superScriptMapper[type][planType]?.individual;

      const familyDeductibleSuperScript =
        superScriptMapper[type][planType]?.family;

      const renderValueWithSuperscript = (
        value: string,
        superscript: React.ReactNode
      ) => {
        return value !== defaultValue ? (
          <>
            {value} {superscript}
          </>
        ) : (
          value
        );
      };

      const coverageValue = (
        <>
          {specialization === 'individual' ? (
            renderValueWithSuperscript(
              individualValue,
              individualDeductibleSuperScript
            )
          ) : specialization === 'family' ? (
            renderValueWithSuperscript(familyValue, familyDeductibleSuperScript)
          ) : // fallback if not explicitly provided
          type === DEDUCTIBLE || type === OUT_OF_POCKET ? (
            <>
              {renderValueWithSuperscript(
                individualValue,
                individualDeductibleSuperScript
              )}{' '}
              /{' '}
              {renderValueWithSuperscript(
                familyValue,
                familyDeductibleSuperScript
              )}
            </>
          ) : (
            renderValueWithSuperscript(
              individualValue,
              individualDeductibleSuperScript
            )
          )}
        </>
      );

      const deductibleTitlesToExclude = notApplicableTitles
        .filter((title) => title.startsWith('MEDICAL_DEDUCTIBLE_'))
        .map((title) => title.replace('MEDICAL_DEDUCTIBLE_', ''));

      const filteredDeductibleLabels = MOBILE_DEDUCTIBLE_LABELS.filter(
        (deductible) => !deductibleTitlesToExclude.includes(deductible.label)
      );

      const matchedLabel = filteredDeductibleLabels.find(
        (deductible) =>
          deductible.value === type &&
          deductible.fieldSpecialization === specialization
      );

      const displayLabel = getText(
        SPECIALIZED_FIELD_KEYS?.[type as keyof typeof SPECIALIZED_FIELD_KEYS]?.[
          specialization ?? 'individual'
        ],
        language
      );

      // check for Deductible (Ind within Fam)
      const isDeductibleIndWithinFamMissing = !notApplicableTitles.includes(
        'MEDICAL_DEDUCTIBLE_Deductible (Ind within Fam)'
      );
      const isIndividualOrFamilyDeductibleAvailable =
        !notApplicableTitles.includes(
          'MEDICAL_DEDUCTIBLE_Individual Deductible'
        ) ||
        !notApplicableTitles.includes('MEDICAL_DEDUCTIBLE_Family Deductible');
      const isFirstMatch =
        filteredDeductibleLabels.findIndex(
          (deductible) =>
            deductible.value === type &&
            deductible.fieldSpecialization === specialization
        ) === 0;

      const tooltip =
        specialization === 'individual'
          ? individualValue
            ? `${individualValue} ${getSupTextFromReactNode(
                individualDeductibleSuperScript
              )}`
            : '-'
          : specialization === 'family'
          ? familyValue
            ? `${familyValue} ${getSupTextFromReactNode(
                familyDeductibleSuperScript
              )}`
            : '-'
          : type === DEDUCTIBLE || type === OUT_OF_POCKET
          ? `${
              individualValue
                ? `${individualValue} ${getSupTextFromReactNode(
                    individualDeductibleSuperScript
                  )}`
                : '-'
            } / ${
              familyValue
                ? `${familyValue} ${getSupTextFromReactNode(
                    familyDeductibleSuperScript
                  )}`
                : '-'
            }`
          : individualValue
          ? `${individualValue} ${getSupTextFromReactNode(
              individualDeductibleSuperScript
            )}`
          : '-';

      const deductibleDentalTitlesToExclude = notApplicableTitles
        .filter((title) => title.startsWith('DENTAL_DEDUCTIBLE_'))
        .map((title) => title.replace('DENTAL_DEDUCTIBLE_', ''));
      const filteredDentalDeductibleLabels =
        MOBILE_DENTAL_DEDUCTIBLE_LABELS.filter(
          (deductible) =>
            !deductibleDentalTitlesToExclude.includes(deductible.label)
        );

      const isFirstMatchDental =
        planType === MDVTypes.DENTAL &&
        filteredDentalDeductibleLabels.length > 0 &&
        matchedLabel?.label === filteredDentalDeductibleLabels[0].label;

      return (
        <>
          {!isEditMode && isMobile && matchedLabel && (
            <>
              {isFirstMatch &&
                planType === MDVTypes.MEDICAL &&
                (isIndividualOrFamilyDeductibleAvailable ||
                  (!isIndividualOrFamilyDeductibleAvailable &&
                    !isDeductibleIndWithinFamMissing)) && (
                  <div className={styles.mobileSectionHeader}>
                    {getText(
                      DEDUCTIBLES_AND_OOP_MAXIMUM,
                      language
                    )?.toUpperCase()}
                  </div>
                )}
              {isFirstMatchDental && (
                <div className={styles.mobileSectionHeader}>
                  {getText(
                    DEDUCTIBLES_BENEFIT_MAXIMUMS,
                    language
                  )?.toUpperCase()}
                </div>
              )}
              <div className={styles.serviceHeaderMobile}>{displayLabel}</div>
            </>
          )}

          <div
            ref={ref}
            style={!isMobile ? { height } : {}}
            className={`${styles.benefitDetail} ${
              isEditMode ? styles.benefitDetailTruncated : ''
            }`}
            title={tooltip}
          >
            {!isEditMode
              ? coverageValue
              : getCoverageDetails(
                  type,
                  individualValue,
                  familyValue,
                  individualDeductibleSuperScript,
                  familyDeductibleSuperScript,
                  specialization
                )}
          </div>
        </>
      );
    };
    const getCoverageDetails = (
      type: string,
      individualValue: string,
      familyValue?: string,
      individualDeductibleSuperScript?: JSX.Element,
      familyDeductibleSuperScript?: JSX.Element,
      specialization: 'individual' | 'family' = 'individual'
    ) => {
      const isIndividual = specialization === IS_INDIVIDUAL;
      const value = isIndividual ? individualValue : familyValue;
      const superscript = isIndividual
        ? individualDeductibleSuperScript
        : familyDeductibleSuperScript;

      const isMissing =
        value === defaultValue ||
        value === NOT_APPLICABLE_FORMATTED ||
        value === NOT_COVERED_FORMATTED ||
        value === DOUBLE_DASH ||
        value === TRIPLE_DASH ||
        value === NO_DATA;
      if (isMissing) {
        return (
          <div className={styles.commonMissingTag}>
            <MissingInfo missingInfoText="Missing Info" />
          </div>
        );
      }

      return (
        <>
          {value} {superscript}
        </>
      );
    };

    const getCoverageValue = (
      type: string,
      isIndividual: boolean,
      applicableText: 'Applicable' | 'Cost'
    ) => {
      if (type === DEDUCTIBLE || type === OUT_OF_POCKET) {
        if (isIndividual) {
          return get(
            plan,
            `${type}.individual${networkTextPrefix}Network${applicableText}`
          );
        } else {
          return get(
            plan,
            `${type}.family${networkTextPrefix}Network${applicableText}`
          );
        }
      } else if (type === IND_WITHIN_FAMILY_DEDUCTIBLE) {
        return get(plan, `${type}.${networkType}Network${applicableText}`);
      } else {
        return get(plan, `${type}.${networkType}Network${applicableText}`);
      }
    };

    const getMDVChannel = () => {
      if (planType === MDVTypes.MEDICAL) {
        return MEDICAL_PLAN_CHANNEL;
      } else if (planType === MDVTypes.DENTAL) {
        return DENTAL_PLAN_CHANNEL;
      } else {
        return VISION_PLAN_CHANNEL;
      }
    };

    const excludeIfCustomServicesUnavailable = (title: MDVMetaService) => {
      const { excludeIfUnavailable } = title;
      let isExcluded = false;
      excludeIfUnavailable &&
        excludeIfUnavailable.forEach((serviceCode: number) => {
          if (serviceCode === 0 && isEditMode) {
            return (isExcluded = additionalServices.length > 0);
          }
          if (
            nonExistCustomServices &&
            nonExistCustomServices.hasOwnProperty(
              nonExistCustomServices[serviceCode]
            ) &&
            attachedPlans.length !== nonExistCustomServices[serviceCode]
          ) {
            return (isExcluded = true);
          }
        });
      return isExcluded;
    };

    const dbgGroups = currentBenguide?.benefitClasses;
    const dbgRatesGroups = plan?.groups?.filter((plan: any) =>
      dbgGroups.includes(plan)
    );
    const rateGroup = plan.rates && plan.rates[dbgRatesGroups[0]];
    const { type, fourTierContributions, ntierContributions } = rateGroup || {};

    // Get coverageLabel based on tier type
    const getCoverageLabel = () => {
      if (!selectedCoverageType) return '';

      // For Four-Tier, use predefined labels based on fourTierValue
      if (type === FOUR_TIER) {
        const fourTierLabels = {
          EE: getText(COVERAGE_JUST_YOU, language),
          ES:
            selectedCoverageType.showBothTierValues &&
            selectedCoverageType.alternativeFourTierValue &&
            type === FOUR_TIER
              ? getText(COVERAGE_YOU_SPOUSE_OR_CHILDREN, language)
              : getText(COVERAGE_YOU_SPOUSE, language),
          EC: getText(COVERAGE_YOU_CHILDREN, language),
          EF: getText(COVERAGE_YOU_FAMILY, language),
        };

        return (
          fourTierLabels[
            selectedCoverageType.fourTierValue as keyof typeof fourTierLabels
          ] ||
          selectedCoverageType.coverageLabel ||
          ''
        );
      }

      // For N-Tier or other types, use the existing coverageLabel from selectedCoverageType
      return selectedCoverageType.coverageLabel || '';
    };

    const coverageLabel = getCoverageLabel();
    const contribution =
      (type === FOUR_TIER ? fourTierContributions : ntierContributions) || {};
    const { contributions = [] } = contribution;
    const contributionFiled =
      type === FOUR_TIER
        ? selectedCoverageType.fourTierValue
        : selectedCoverageType.nTierValue;
    const value =
      contributions.find((tier: any) => tier.tierName === contributionFiled) ||
      {};
    const { totalCost } = value;

    // Handle dual tier values for Y1C (ES and EC)
    const getAlternativeValue = () => {
      if (
        selectedCoverageType.showBothTierValues &&
        selectedCoverageType.alternativeFourTierValue &&
        type === FOUR_TIER
      ) {
        const alternativeValue =
          contributions.find(
            (tier: any) =>
              tier.tierName === selectedCoverageType.alternativeFourTierValue
          ) || {};
        return alternativeValue;
      }
      return null;
    };

    const alternativeValue = getAlternativeValue();
    const { totalCost: alternativeTotalCost } = alternativeValue || {};

    const getHSAContribution = (
      hsaMedicalContribution: HSAMedicalContribution,
      index: number
    ) => {
      const otherContribution =
        hsaMedicalContribution && hsaMedicalContribution.otherContribution;
      const employerEEContribution =
        hsaMedicalContribution && hsaMedicalContribution.employerEEContribution;
      const employerESContribution =
        hsaMedicalContribution && hsaMedicalContribution.employerESContribution;
      const employerECContribution =
        hsaMedicalContribution && hsaMedicalContribution.employerECContribution;
      const employerEFContribution =
        hsaMedicalContribution && hsaMedicalContribution.employerEFContribution;
      const frequency =
        hsaMedicalContribution && hsaMedicalContribution.frequency;
      const noPlansTagged =
        hsaMedicalContribution && hsaMedicalContribution.noPlansTagged;
      const multiplePlansTagged =
        hsaMedicalContribution && hsaMedicalContribution.multiplePlansTagged;
      const benefitKind =
        hsaMedicalContribution && hsaMedicalContribution.benefitKind;

      const noValuesPresent =
        !noPlansTagged &&
        !multiplePlansTagged &&
        isEmpty(otherContribution) &&
        !employerEEContribution &&
        !employerECContribution &&
        !employerESContribution &&
        !employerEFContribution;

      const getFrequencyValue = () => {
        if (frequency === 'ANNUALLY') {
          return 'YR';
        } else if (frequency === 'MONTHLY') {
          return 'MTH';
        } else if (frequency === 'SEMI_MONTHLY') {
          return 'SM';
        } else if (frequency === 'WEEKLY') {
          return 'W';
        } else if (frequency === 'BI_WEEKLY') {
          return 'BW';
        } else if (frequency === 'QUARTERLY') {
          return 'QTR';
        }
      };

      const tootTipText = `Your employer will contribute this amount on a ${
        FORMATTED_FREQUENCY[frequency] || ''
      } basis`;

      const contributionValue = () => {
        if (selectedCoverageType.fourTierValue === 'EE') {
          return employerEEContribution;
        } else if (selectedCoverageType.fourTierValue === 'ES') {
          return employerESContribution;
        } else if (selectedCoverageType.fourTierValue === 'EC') {
          return employerECContribution;
        } else if (selectedCoverageType.fourTierValue === 'EF') {
          return employerEFContribution;
        }
      };

      const navigateToHSA = () => {
        if (benefitKind === 'HSA') {
          navigate(
            `/${urlHash}${
              currentBenguide.employerName
                ? '/' + currentBenguide.employerName
                : ''
            }/tax-advantaged/hsa`
          );
        }
      };

      const getContributionValue = () => {
        if (benefitKind === 'HSA') {
          if (contributionValue()) {
            return `$${contributionValue()} HSA CONTRIBUTION/ ${getFrequencyValue()}`;
          } else {
            return 'HSA CONTRIBUTION';
          }
        }
      };

      const planCompatible = benefitKind === 'HSA' ? plan.hsaCompatible : true;

      const isSelectedCoveredValueEmpty =
        (selectedCoverageType.fourTierValue === 'EE' &&
          employerEEContribution === undefined) ||
        (selectedCoverageType.fourTierValue === 'ES' &&
          employerESContribution === undefined) ||
        (selectedCoverageType.fourTierValue === 'EC' &&
          employerECContribution === undefined) ||
        (selectedCoverageType.fourTierValue === 'EF' &&
          employerEFContribution === undefined);

      // For dual tier values (Y1C), check if either ES or EC has values - only for FOUR_TIER
      const hasAlternativeContribution =
        selectedCoverageType.showBothTierValues &&
        selectedCoverageType.alternativeFourTierValue === 'EC' &&
        employerECContribution !== undefined &&
        type === FOUR_TIER;

      return noValuesPresent ||
        !planCompatible ||
        (isSelectedCoveredValueEmpty && !hasAlternativeContribution) ? (
        <></>
      ) : (
        <>
          {!noPlansTagged &&
            benefitKind === 'HSA' &&
            !multiplePlansTagged &&
            planType === MDVTypes.MEDICAL && (
              <div
                className={`${styles.hsaContribution} ${
                  index === 1 ? styles.hra : styles.hsa
                }`}
                onClick={() => {
                  navigateToHSA();
                }}
              >
                {!isEditMode && contributionValue() !== undefined ? (
                  <Popover
                    content={<div>{tootTipText}</div>}
                    placement="left"
                    overlayClassName={`popoverInner ${styles.planWrapper}`}
                  >
                    <div className={styles.hsaValue}>
                      {getContributionValue()}
                    </div>
                  </Popover>
                ) : (
                  <div className={styles.hsaValue}>
                    {getContributionValue()}
                  </div>
                )}
              </div>
            )}
        </>
      );
    };

    const displayTaxAdvantageTag = (
      taxAdvantageContribution: HSAMedicalContribution[],
      taxAdvantageType: TaxAdvantageBenefitKind
    ) => {
      if (
        !isEmpty(taxAdvantageContribution) &&
        taxAdvantageContribution.length <= 1
      ) {
        return taxAdvantageContribution.map(
          (hsaMedicalContribution: HSAMedicalContribution, index: number) => {
            return (
              <div key={index}>
                {getHSAContribution(hsaMedicalContribution, index)}
              </div>
            );
          }
        );
      } else if (isEditMode && !isEmpty(taxAdvantageContribution)) {
        return (
          <div className={styles.hraConflict}>
            <MissingInfo
              missingInfoText={`${taxAdvantageType} Contribution Conflict`}
            />
          </div>
        );
      }
    };

    const getRXCoverageValue = (
      type: string,
      isIndividual: boolean,
      isOop: boolean,
      applicableText: 'Applicable' | 'Cost'
    ) => {
      if (!isOop) {
        if (isIndividual) {
          return get(
            plan,
            `${type}.individualDeductible${networkTextPrefix}Network${applicableText}`
          );
        } else {
          return get(
            plan,
            `${type}.familyDeductible${networkTextPrefix}Network${applicableText}`
          );
        }
      } else {
        if (isIndividual) {
          return get(
            plan,
            `${type}.individualOopMax${networkTextPrefix}Network${applicableText}`
          );
        } else {
          return get(
            plan,
            `${type}.familyOopMax${networkTextPrefix}Network${applicableText}`
          );
        }
      }
    };

    const showRxOopDeductiblesValue = (
      type: string,
      isOop: boolean,
      fieldName: keyof SuperScriptMapDeductibles,
      specialization: 'individual' | 'family' = 'individual'
    ) => {
      let individualValue = coverageFormatter(
        getRXCoverageValue(type, true, isOop, COST),
        getRXCoverageValue(type, true, isOop, APPLICABLE)
      );
      let familyValue = coverageFormatter(
        getRXCoverageValue(type, false, isOop, COST),
        getRXCoverageValue(type, false, isOop, APPLICABLE)
      );

      if (
        [
          NOT_APPLICABLE_FORMATTED,
          NOT_COVERED_FORMATTED,
          DOUBLE_DASH,
          TRIPLE_DASH,
          NO_DATA,
        ].includes(individualValue)
      ) {
        individualValue = defaultValue;
      }
      if (
        [
          NOT_APPLICABLE_FORMATTED,
          NOT_COVERED_FORMATTED,
          DOUBLE_DASH,
          TRIPLE_DASH,
          NO_DATA,
        ].includes(familyValue)
      ) {
        familyValue = defaultValue;
      }

      const network = networkType === IN_NETWORK.value ? 'IN' : 'OUT';
      const labelParams = plan?.planLabels?.parameters ?? [];

      const getSuperscript = (key: string) => (
        <sup>
          {getLabelsSuperscriptWithKey(
            labelParams,
            key,
            network!
          )?.toString() || ''}
        </sup>
      );

      const inNetwork = network === 'IN';

      const superScriptMapIndividualMap: SuperScriptMapDeductibles = {
        [RX_DEDUCTIBLES]: getSuperscript(
          inNetwork
            ? MEDICAL_RX_INDIVIDUAL_DEDUCTIBLE_IN
            : MEDICAL_RX_INDIVIDUAL_DEDUCTIBLE_OUT
        ),
        [RX_DEDUCTIBLE_AND_OOP_ATTRIBUTE_NAME]: getSuperscript(
          inNetwork
            ? MEDICAL_RX_INDIVIDUAL_OOP_MAX_IN
            : MEDICAL_RX_INDIVIDUAL_OOP_MAX_OUT
        ),
        [RX_OOP_MAX]: getSuperscript(
          inNetwork
            ? MEDICAL_RX_INDIVIDUAL_OOP_MAX_IN
            : MEDICAL_RX_INDIVIDUAL_OOP_MAX_OUT
        ),
      };

      const superScriptMapFamilyMap: SuperScriptMapDeductibles = {
        [RX_DEDUCTIBLES]: getSuperscript(
          inNetwork
            ? MEDICAL_RX_FAMILY_DEDUCTIBLE_IN
            : MEDICAL_RX_FAMILY_DEDUCTIBLE_OUT
        ),
        [RX_DEDUCTIBLE_AND_OOP_ATTRIBUTE_NAME]: getSuperscript(
          inNetwork
            ? MEDICAL_RX_FAMILY_OOP_MAX_IN
            : MEDICAL_RX_FAMILY_OOP_MAX_OUT
        ),
        [RX_OOP_MAX]: getSuperscript(
          inNetwork
            ? MEDICAL_RX_FAMILY_OOP_MAX_IN
            : MEDICAL_RX_FAMILY_OOP_MAX_OUT
        ),
      };

      const isIndividual = specialization === IS_INDIVIDUAL;
      const value = isIndividual ? individualValue : familyValue;
      const superscript = isIndividual
        ? superScriptMapIndividualMap[fieldName]
        : superScriptMapFamilyMap[fieldName];

      const isMissing = value === defaultValue;

      const coverageValue = isMissing ? (
        isEditMode ? (
          <div className={styles.commonMissingTag}>
            <MissingInfo missingInfoText="Missing Info" />
          </div>
        ) : (
          <span className={styles.emptyValueRx}>{defaultValue}</span>
        )
      ) : (
        <>
          {value} {superscript}
        </>
      );

      const editCoverageValue = `${value} ${getSupTextFromReactNode(
        superscript
      )}`;
      const toolTip =
        isMissing && !isEditMode ? defaultValue : editCoverageValue;

      const rxTitlesToExclude = notApplicableTitles
        .filter((title) => title.startsWith('MEDICAL_RX_'))
        .map((title) => title.replace('MEDICAL_RX_', ''));

      const filteredRxLabels = MOBILE_RX_DEDUCTIBLE_OOP_LABELS.filter(
        (deductible) => !rxTitlesToExclude.includes(deductible.label)
      );

      const matchedLabel = filteredRxLabels.find(
        (deductible) =>
          deductible.value === fieldName &&
          deductible.fieldSpecialization === specialization
      );

      const displayLabel =
        getText(
          SPECIALIZED_FIELD_KEYS?.[
            fieldName as keyof typeof SPECIALIZED_FIELD_KEYS
          ]?.[specialization ?? 'individual'],
          language
        ) ?? matchedLabel?.label;

      // Find first available deductible/OOP label (priority ordered)
      const orderedFields: {
        value: keyof SuperScriptMapDeductibles;
        fieldSpecialization: 'individual' | 'family';
      }[] = [
        { value: RX_DEDUCTIBLES, fieldSpecialization: 'individual' },
        { value: RX_DEDUCTIBLES, fieldSpecialization: 'family' },
        { value: RX_OOP_MAX, fieldSpecialization: 'individual' },
        { value: RX_OOP_MAX, fieldSpecialization: 'family' },
      ];

      const firstAvailable = orderedFields.find(
        (item) =>
          !notApplicableTitles.includes(
            `MEDICAL_RX_${
              MOBILE_RX_DEDUCTIBLE_OOP_LABELS.find(
                (label) =>
                  label.value === item.value &&
                  label.fieldSpecialization === item.fieldSpecialization
              )?.label || ''
            }`
          )
      );

      const shouldShowRxHeader =
        firstAvailable?.value === fieldName &&
        firstAvailable?.fieldSpecialization === specialization;

      return (
        <>
          {!isEditMode && isMobile && matchedLabel && (
            <>
              {shouldShowRxHeader && (
                <div className={styles.mobileSectionHeader}>
                  {getText(RX, language)}
                </div>
              )}
              <div className={styles.serviceHeaderMobile}>{displayLabel}</div>
            </>
          )}
          <div
            className={`${styles.benefitDetail} ${styles.benefitDetailTruncated}`}
            title={toolTip}
          >
            {!isEditMode
              ? coverageValue
              : getRxCoverageDetails(editCoverageValue, coverageValue)}
          </div>
        </>
      );
    };

    const getRxCoverageDetails = (
      individualValue: string,
      defaultValueElement: JSX.Element
    ) => {
      if (
        individualValue === defaultValue ||
        individualValue === NOT_APPLICABLE_FORMATTED ||
        individualValue === NOT_COVERED_FORMATTED ||
        individualValue === DOUBLE_DASH ||
        individualValue === TRIPLE_DASH ||
        individualValue === NO_DATA
      ) {
        return (
          <div className={styles.commonMissingTag}>
            <MissingInfo missingInfoText="Missing Info" />
          </div>
        );
      } else {
        return defaultValueElement;
      }
    };

    const getIndividualWithinCoverageDetails = (
      type: string,
      individualValue: string,
      defaultElement: JSX.Element
    ) => {
      if (type === DEDUCTIBLE || type === OUT_OF_POCKET) {
        if (
          individualValue === defaultValue ||
          individualValue === NOT_APPLICABLE_FORMATTED ||
          individualValue === NOT_COVERED_FORMATTED ||
          individualValue === DOUBLE_DASH ||
          individualValue === TRIPLE_DASH ||
          individualValue === NO_DATA
        )
          return (
            <div className={styles.commonMissingTag}>
              <MissingInfo missingInfoText="Missing Info" />
            </div>
          );
        else return defaultElement;
      }
    };

    const getIndividualWithinCoverageValue = (
      type: string,
      applicableText: 'Applicable' | 'Cost'
    ) => {
      return (
        get(
          plan,
          `${type}.individualWithinFamily${networkTextPrefix}Network${applicableText}`
        ) || defaultValue
      );
    };

    const showIndividualWithinCoverageValue = (
      type: string,
      fieldName: keyof SuperScriptMapIndFamilyWithin,
      ref?: any,
      height?: number,
      title?: any
    ) => {
      // This covers services oop and deductible ind-within vals

      const individualValue = coverageFormatter(
        getIndividualWithinCoverageValue(type, COST),
        getIndividualWithinCoverageValue(type, APPLICABLE)
      );

      const network = networkType === IN_NETWORK.value ? 'IN' : 'OUT';
      const labelParams = plan?.planLabels?.parameters ?? [];

      const getSuperscript = (key: string) => {
        return (
          <sup>
            {getLabelsSuperscriptWithKey(
              labelParams,
              key,
              network!
            )?.toString() || ''}
          </sup>
        );
      };

      const inNetwork = network === 'IN';

      const superScriptMapForIndFamilyWithin: SuperScriptMapIndFamilyWithin = {
        [IND_WITHIN_FAMILY_DEDUCTIBLE]: getSuperscript(
          inNetwork
            ? MEDICAL_INDIVIDUAL_WITHIN_FAMILY_DEDUCTIBLE_IN
            : MEDICAL_INDIVIDUAL_WITHIN_FAMILY_DEDUCTIBLE_OUT
        ),
        [IND_WITHIN_FAMILY_OOP_MAX]: getSuperscript(
          inNetwork
            ? MEDICAL_INDIVIDUAL_WITHIN_FAMILY_OOP_MAX_IN
            : MEDICAL_INDIVIDUAL_WITHIN_FAMILY_OOP_MAX_OUT
        ),
      };

      const editCoverageValue =
        individualValue === NOT_APPLICABLE_FORMATTED ||
        individualValue === NOT_COVERED_FORMATTED ||
        individualValue === DOUBLE_DASH ||
        individualValue === TRIPLE_DASH ||
        individualValue === NO_DATA
          ? defaultValue
          : individualValue;

      const coverageValue = (
        <>
          {individualValue === NOT_APPLICABLE_FORMATTED ||
          individualValue === NOT_COVERED_FORMATTED ||
          individualValue === DOUBLE_DASH ||
          individualValue === TRIPLE_DASH ||
          individualValue === NO_DATA ? (
            defaultValue
          ) : (
            <>
              {individualValue !== defaultValue ? (
                <>
                  {individualValue}{' '}
                  {superScriptMapForIndFamilyWithin[fieldName]}
                </>
              ) : (
                individualValue
              )}
            </>
          )}
        </>
      );

      const tooltip = `${
        editCoverageValue
          ? `${editCoverageValue} ${`${getSupTextFromReactNode(
              superScriptMapForIndFamilyWithin[fieldName]
            )}`}`
          : '-'
      }`;

      const isDeductibleCostsMissing = [
        'MEDICAL_DEDUCTIBLE_Individual Deductible',
        'MEDICAL_DEDUCTIBLE_Family Deductible',
      ].every((title) => notApplicableTitles.includes(title));

      const isOopMaxCostsMissing = [
        'MEDICAL_DEDUCTIBLE_Individual Deductible',
        'MEDICAL_DEDUCTIBLE_Family Deductible',
        'MEDICAL_DEDUCTIBLE_Deductible (Ind within Fam)',
        'MEDICAL_DEDUCTIBLE_Individual Out-of-Pocket Max',
        'MEDICAL_DEDUCTIBLE_Family Out-of-Pocket Max',
      ].every((title) => notApplicableTitles.includes(title));

      const label = MOBILE_IND_DEDUCTIBLE_LABELS.find(
        (deductible) => deductible.value === fieldName
      )?.label;

      // Check if this is the first field with its label
      const isFirstMatchingLabel =
        MOBILE_IND_DEDUCTIBLE_LABELS.findIndex(
          (deductible) => deductible.label === label
        ) ===
        MOBILE_IND_DEDUCTIBLE_LABELS.findIndex(
          (deductible) => deductible.value === fieldName
        );

      const shouldShowDeductibleHeader =
        isDeductibleCostsMissing &&
        label === 'Deductible (Ind within Fam)' &&
        isFirstMatchingLabel;

      const shouldShowOopMaxHeader =
        isOopMaxCostsMissing &&
        label === 'Out-of-Pocket Max (Ind within Fam)' &&
        isFirstMatchingLabel;

      const internationaliedLabel =
        getText(
          SPECIALIZED_FIELD_KEYS?.[
            title?.fieldName as keyof typeof SPECIALIZED_FIELD_KEYS
          ]?.['individual'],
          language
        ) ?? label;

      return (
        <>
          {!isEditMode &&
            isMobile &&
            (shouldShowDeductibleHeader || shouldShowOopMaxHeader) && (
              <div className={styles.mobileSectionHeader}>
                {getText(DEDUCTIBLES_AND_OOP_MAXIMUM, language)}
              </div>
            )}

          {!isEditMode && isMobile && (
            <div className={styles.serviceHeaderMobile}>
              {internationaliedLabel}
            </div>
          )}

          <div
            style={!isMobile ? { height } : {}}
            ref={ref}
            className={`${styles.benefitDetail} ${
              isEditMode ? styles.benefitDetailTruncated : ''
            }`}
            title={tooltip}
          >
            {!isEditMode
              ? coverageValue
              : getIndividualWithinCoverageDetails(
                  type,
                  editCoverageValue,
                  coverageValue
                )}
          </div>
        </>
      );
    };

    const getPlanValues = (title: any) => {
      const {
        fieldName,
        fieldValue,
        serviceBenefitCode,
        rxCode,
        fieldFormatter,
        additionalServices: isAdditionalServicesSupport,
        includeIfServiceAvailable,
        isRx,
      } = title;

      const contribFreq = findFrequencyByBenefitKind(
        currentBenguide.contributionFrequencies,
        planType.toUpperCase()
      );
      const { enabled } = contribFreq;
      const attachedServices = attachedPlans
        .reduce((previousValue: any, customService: any): any => {
          return [...previousValue, ...customService.customServices];
        }, [])
        .map((service: any) => service?.benefitCode?.code);

      if (fieldName === 'planNetwork') {
        if (notApplicableTitles.includes('UnavailablePlanNetwork')) {
          return (
            <div
              ref={(el) => {
                (planNetworkRef as any).current = el;
                planNetworkHeightRef.current = el;
              }}
              style={
                !isMobile
                  ? {
                      height: planNetworkHeight,
                      minHeight: planNetworkHeight || '32px',
                    }
                  : {}
              }
              className={styles.emptyPlanNetwork}
            >
              {/* Empty plan network placeholder to maintain height sync */}
            </div>
          );
        }

        return (
          <EditableHotspot
            key={fieldName}
            alwaysVisible={false}
            editIcon={<img src={iconEdit} alt="edit" />}
            editText="Edit"
            useChannel={{
              client: upClient,
              channel: getMDVChannel(),
              event: dataGroups[0].event,
              data: { ...plan, planIds: planIds },
              dbgMetaData: benguide,
            }}
            dataTestId={`${fieldName}-plan${dataIndex}`}
          >
            <div className={getRowWrapperClass(true)}>
              <Tooltip title={plan?.planNetworkName} placement="right">
                {isMobile && !isEditMode && (
                  <span className={styles.mobilePlanNetwork}>
                    {getText(PLAN_NETWORK, language)}
                  </span>
                )}
                <div
                  ref={(el) => {
                    (planNetworkRef as any).current = el;
                    planNetworkHeightRef.current = el;
                  }}
                  style={
                    !isMobile
                      ? {
                          height: planNetworkHeight,
                          minHeight:
                            planNetworkHeight ||
                            (!plan?.planNetworkName ? '32px' : undefined),
                        }
                      : {}
                  }
                  className={
                    isMobile
                      ? `${styles.planNetworkMobile} ${
                          isEditMode ? styles.planNetworkMobileTruncated : ''
                        }`
                      : `${styles.planNetwork} ${
                          isEditMode ? styles.planNetworkTruncated : ''
                        }`
                  }
                >
                  {plan?.planNetworkName}
                </div>
              </Tooltip>
            </div>
          </EditableHotspot>
        );
      }
      if (fieldName === 'spaceField' && includeIfServiceAvailable) {
        if (
          attachedServices.some((r: any) =>
            includeIfServiceAvailable?.includes(r)
          ) &&
          !isMobile
        ) {
          return <div className={styles.titleSeperator}></div>;
        } else {
          return null;
        }
      }
      if (
        title.type === 'header' &&
        !hideHeader(
          planType,
          notApplicableTitles,
          title.fieldValue,
          selectedNetwork === 'IN_NETWORK' ? 'IN' : 'OUT'
        )
      ) {
        const isLastItem = dataIndex === attachedPlans.length - 1;
        return (
          <div
            className={`${styles.headerSeperator} ${
              isLastItem ? styles.headerSeparatorWithBorder : ''
            }`}
          >
            <div className={styles.titleSeperator}></div>
            {'-'}
          </div>
        );
      }
      if (!isEditMode && notApplicableTitles.includes(title.fieldValue)) {
        return null;
      }

      // Hide field if it's only available for specific network and current network doesn't match
      const networkType = selectedNetwork === 'IN_NETWORK' ? 'IN' : 'OUT';
      if (title.availableOnly && title.availableOnly !== networkType) {
        return null;
      }

      if (fieldName === 'title') {
        return getMDVBasicInfo(enabled);
      } else if (serviceBenefitCode) {
        return getServiceText(
          serviceBenefitCode,
          customServices,
          fieldFormatter,
          title
        );
      } else if (isEditMode && isRx) {
        return <></>;
      } else if (rxCode) {
        return getRxText(rxCode, rxCosts, fieldFormatter, fieldName);
      } else if (fieldName === DEDUCTIBLE_COST) {
        return showCoverageDetails(
          DEDUCTIBLE,
          title.fieldSpecialization,
          title.fieldSpecialization === 'individual'
            ? deductiblesIndividualHeightRef
            : deductiblesFamilyHeightRef,
          title.fieldSpecialization === 'individual'
            ? deductiblesIndividualHeight
            : deductiblesFamilyHeight
        );
      } else if (fieldName === IND_WITHIN_FAMILY_DEDUCTIBLE) {
        return showIndividualWithinCoverageValue(
          DEDUCTIBLE,
          fieldName,
          deductibleWithinHeightRef,
          deductibleWithinHeight,
          title
        );
      } else if (fieldName === RX_OOP_MAX) {
        return showRxOopDeductiblesValue(
          RX_DEDUCTIBLE_AND_OOP_ATTRIBUTE_NAME,
          true,
          fieldName,
          title.fieldSpecialization
        );
      } else if (fieldName === RX_DEDUCTIBLES) {
        return showRxOopDeductiblesValue(
          RX_DEDUCTIBLE_AND_OOP_ATTRIBUTE_NAME,
          false,
          fieldName,
          title.fieldSpecialization
        );
      } else if (fieldName === OUT_OF_POCKET_MAX) {
        return showCoverageDetails(
          OUT_OF_POCKET,
          title.fieldSpecialization,
          title.fieldSpecialization === 'individual'
            ? oopMaxIndividualHeightRef
            : oopMaxFamilyHeightRef,
          title.fieldSpecialization === 'individual'
            ? oopMaxIndividualHeight
            : oopMaxFamilyHeight
        );
      } else if (fieldName === IND_WITHIN_FAMILY_OOP_MAX) {
        return showIndividualWithinCoverageValue(
          OUT_OF_POCKET,
          fieldName,
          oopWithinHeightRef,
          oopWithinHeight,
          title
        );
      } else if (
        !fieldValue &&
        !isAdditionalServicesSupport &&
        (!title.excludeIfUnavailable ||
          (title.excludeIfUnavailable &&
            excludeIfCustomServicesUnavailable(title))) &&
        !isMobile
      ) {
        return <div className={styles.titleSeperator}></div>;
      } else if (fieldName === MONTHLY_COST) {
        if (
          !isEditMode &&
          (!enabled ||
            rateValidations.hasMismatchContributions ||
            rateValidationInProgress)
        ) {
          return <></>;
        }
        if (!enabled || rateValidations.hasMismatchContributions) {
          return (
            <div
              ref={monthlyCostHeightRef}
              style={!isMobile ? { height: monthlyCostHeight } : {}}
              className={`${styles.monthlyCost} ${styles.monthlyCostDisabled}`}
            >
              <div className={styles.monthlyCostValue}>
                {NDASH} {NDASH}
              </div>

              <div className={styles.monthlyCoversYou}>{coverageLabel}</div>
            </div>
          );
        }
        if (type === AGE_BAND) {
          return (
            <div
              ref={monthlyCostHeightRef}
              style={!isMobile ? { height: monthlyCostHeight } : {}}
              className={styles.monthlyCost}
              title={getText(AGE_BAND, language)}
            >
              <div className={styles.monthlyCostValue}>
                {getText(AGE_BAND, language)}
              </div>

              <div className={styles.monthlyCoversYou}>{coverageLabel}</div>
            </div>
          );
        }
        const isContributionsExist = contributions.some(
          (item: { totalCost: number }) => item.totalCost > 0
        );

        if (!totalCost && isEditMode && !isContributionsExist) {
          return (
            <div
              ref={monthlyCostHeightRef}
              style={!isMobile ? { height: monthlyCostHeight } : {}}
              className={`${styles.monthlyCostMissingInfoWrapper} ${styles.monthlyCost}`}
            >
              <MissingInfo missingInfoText="Missing Info" />
            </div>
          );
        }
        const contribFreq = findFrequencyByBenefitKind(
          currentBenguide?.contributionFrequencies,
          planType?.toUpperCase()
        );
        const { frequency } = contribFreq;
        const employeeContribution = getEEContributionByFrequency(
          frequency,
          value
        );

        // Handle alternative contribution for Y1C dual tier values
        const alternativeEmployeeContribution = alternativeValue
          ? getEEContributionByFrequency(frequency, alternativeValue)
          : null;

        const getMaxValue = () => {
          if (isContributionsExist) {
            const b = maxBy(contributions, getEEContributionField(frequency));
            return getEEContributionByFrequency(frequency, b);
          }
          return 0;
        };

        // Show both ES and EC values for Y1C - only for FOUR_TIER (standard type)
        if (
          selectedCoverageType.showBothTierValues &&
          selectedCoverageType.alternativeFourTierValue &&
          type === FOUR_TIER
        ) {
          return (
            <div
              ref={monthlyCostHeightRef}
              style={!isMobile ? { height: monthlyCostHeight } : {}}
              className={styles.monthlyCost}
            >
              {isMobile && (totalCost || alternativeTotalCost) && frequency && (
                <span className={styles.monthlyFrequency}>
                  {(() => {
                    const freqKey =
                      FREQUENCY_KEYS[frequency as keyof typeof FREQUENCY_KEYS];
                    const freqText = freqKey ? getText(freqKey, language) : '';
                    if (language === 'SPANISH') {
                      return `${getText(YOUR_COST, language)} ${getText(
                        COST_TEXT,
                        language
                      )} ${freqText}`;
                    }
                    return `${getText(
                      YOUR_COST,
                      language
                    )} ${freqText} ${getText(COST_TEXT, language)}`;
                  })()}
                </span>
              )}
              <div className={styles.monthlyCostValue}>
                {totalCost
                  ? currencyFormatter(employeeContribution)
                  : `${NDASH} ${NDASH}`}
                {' / '}
                {alternativeTotalCost
                  ? currencyFormatter(alternativeEmployeeContribution)
                  : `${NDASH} ${NDASH}`}
              </div>
              <div className={styles.monthlyCoversYou}>
                {totalCost || alternativeTotalCost ? coverageLabel : ''}
              </div>
            </div>
          );
        }

        return (
          <div
            ref={monthlyCostHeightRef}
            style={!isMobile ? { height: monthlyCostHeight } : {}}
            className={styles.monthlyCost}
            title={totalCost && currencyFormatter(employeeContribution)}
          >
            {isMobile && totalCost && frequency && (
              <span className={styles.monthlyFrequency}>
                {(() => {
                  const freqKey =
                    FREQUENCY_KEYS[frequency as keyof typeof FREQUENCY_KEYS];
                  const freqText = freqKey ? getText(freqKey, language) : '';
                  if (language === 'SPANISH') {
                    return `${getText(YOUR_COST, language)} ${getText(
                      COST_TEXT,
                      language
                    )} ${freqText}`;
                  }
                  return `${getText(YOUR_COST, language)} ${freqText} ${getText(
                    COST_TEXT,
                    language
                  )}`;
                })()}
              </span>
            )}
            <div className={styles.monthlyCostValue}>
              {totalCost
                ? currencyFormatter(employeeContribution)
                : isContributionsExist
                ? currencyFormatter(getMaxValue())
                : `${NDASH} ${NDASH}`}
            </div>

            <div className={styles.monthlyCoversYou}>
              {totalCost ? coverageLabel : ''}
            </div>
          </div>
        );
      } else if (fieldName === PLAN_YEAR_MAX) {
        return showCoverageDetails(
          PLAN_YEAR_MAX,
          title.fieldSpecialization,
          title.fieldSpecialization === 'individual'
            ? planYearMaxIndividualHeightRef
            : planYearMaxFamilyHeightRef,
          title.fieldSpecialization === 'individual'
            ? planYearMaxIndividualHeight
            : planYearMaxFamilyHeight
        );
      } else if (fieldName === ORTHODONTIA_MAX) {
        return showCoverageDetails(
          ORTHODONTIA_MAX,
          title.fieldSpecialization,
          title.fieldSpecialization === 'individual'
            ? orthodontiaMaxIndividualHeightRef
            : orthodontiaMaxFamilyHeightRef,
          title.fieldSpecialization === 'individual'
            ? orthodontiaMaxIndividualHeight
            : orthodontiaMaxFamilyHeight
        );
      } else if (isAdditionalServicesSupport) {
        return showAdditionalServices();
      }
    };

    const openProviderSearch = () => {
      if (!isEditMode) setIsOpenProviderSearch(true);
    };

    const openPrescriptionDrugList = () => {
      if (!isEditMode) setIsOpenPrescriptionDrugList(true);
    };

    const openPlanTypeDrawer = () => {
      if (!isEditMode) setIsDrawerOpen(true);
    };

    const taggedHRAplan =
      hraPlanList?.find((hraPlans) => hraPlans?.id === plan?.hraPlanId) || null;

    const constructMDVtooltip = () => {
      if (taggedHRAplan) {
        const {
          selectedMedicalPlans = [],
          selectedDentalPlans = [],
          selectedVisionPlans = [],
        } = taggedHRAplan as any;

        const eligiblePlans = [
          !isEmpty(selectedMedicalPlans) && 'Medical',
          !isEmpty(selectedDentalPlans) && 'Dental',
          !isEmpty(selectedVisionPlans) && 'Vision',
        ]
          .filter(Boolean)
          .join(', ');

        return `This HRA allowance is available for eligible ${eligiblePlans} plans.`;
      }
    };

    const getMDVBasicInfo = (ratesEnabled: boolean) => {
      const getHRAContribution = (selectedCoveredType: string) => {
        const employerContribution = (taggedHRAplan as any)
          ?.employerContribution;
        if (taggedHRAplan && employerContribution) {
          switch (selectedCoveredType) {
            case 'EE':
              return employerContribution?.employerEEContribution;
            case 'ES':
              return employerContribution?.employerESContribution;
            case 'EC':
              return employerContribution?.employerECContribution;
            case 'EF':
              return employerContribution?.employerEFContribution;
            case 'EE1':
              return employerContribution?.employerEE1Contribution;
            case 'EE2':
              return employerContribution?.employerEE2Contribution;
            case 'EE3':
              return employerContribution?.employerEE3Contribution;
            case 'EE4':
              return employerContribution?.employerEE4Contribution;
            case 'EE5':
              return employerContribution?.employerEE5Contribution;
            case 'EE6':
              return employerContribution?.employerEE6Contribution;
            default:
              return undefined;
          }
        }
        return undefined;
      };

      const navigateToHRA = () => {
        navigate(
          `/${urlHash}${
            currentBenguide.employerName
              ? '/' + currentBenguide.employerName
              : ''
          }/tax-advantaged/hra`
        );
      };

      const hasPdlFile = (benefitCarrier: any) => {
        const prescriptionDrugListFile = (benefitCarrier as any)
          ?.prescriptionDrugListFile;
        return Boolean(
          prescriptionDrugListFile || benefitCarrier?.prescriptionDrugListUrl
        );
      };

      const displayHRAContributionValue = () => {
        return (
          <div
            onClick={() => {
              navigateToHRA();
            }}
            className={`${styles.hsaContribution} ${styles.hra}`}
          >
            {!isEditMode ? (
              <Popover
                content={<div>{constructMDVtooltip()}</div>}
                placement="left"
                overlayClassName={`popoverInner ${styles.planWrapper}`}
              >
                <div className={styles.hsaValue}>{`$${getHRAContribution(
                  contributionFiled
                )} HRA ALLOWANCE/YR`}</div>
              </Popover>
            ) : (
              <div className={styles.hsaValue}>{`$${getHRAContribution(
                contributionFiled
              )} HRA ALLOWANCE/YR`}</div>
            )}
          </div>
        );
      };
      return (
        <div
          className={`${styles.detailSection} ${styles.basicInfo} ${
            planType === MDVTypes.MEDICAL && styles.medicalBasicInfo
          } ${
            !isEditMode &&
            (!ratesEnabled || rateValidations.hasMismatchContributions) &&
            styles.medicalBasicInfoHiddenRates
          }`}
          ref={detailsSectionRef}
          style={!isMobile && isEditMode ? { height: detailsHeight } : {}}
        >
          <div className={styles.details}>
            <div className={styles.logoWrapper}>
              {plan.benefitCarrier && (
                <img
                  src={plan.benefitCarrier.logoUrl}
                  alt={`${plan.benefitCarrier.name} logo`}
                  className={styles.logo}
                />
              )}
            </div>
            {planType === MDVTypes.MEDICAL && !isEditMode && (
              <div className={styles.compare}>
                {getText(COMPARE, language)}
                <StyledCheckbox
                  checked={selectedPlanForCompare.includes(plan.id)}
                  onChange={(event) => {
                    const { target } = event;
                    const { checked } = target;
                    let selectedPlanForCompareCloned = cloneDeep(
                      selectedPlanForCompare
                    );
                    if (checked) {
                      selectedPlanForCompareCloned.push(plan.id);
                    } else {
                      selectedPlanForCompareCloned =
                        selectedPlanForCompareCloned.filter(
                          (id) => id !== plan.id
                        );
                    }
                    dispatch(
                      setSelectedPlanForCompare(selectedPlanForCompareCloned)
                    );
                  }}
                />
              </div>
            )}
            <div
              ref={planNameHeightRef}
              style={!isMobile ? { height: planNameHeight } : {}}
              className={styles.planNameWrapper}
            >
              <div className={styles.planName} title={name}>
                {name}
              </div>
              {planType === MDVTypes.MEDICAL && (
                <div className={styles.taxAdvantageTags}>
                  {displayTaxAdvantageTag(
                    hsaMedicalContributionVO,
                    TaxAdvantageBenefitKind.HSA
                  )}
                  {displayTaxAdvantageTag(
                    hraMedicalContributionVO,
                    TaxAdvantageBenefitKind.HRA
                  )}
                </div>
              )}
              <div className={styles.taxAdvantageTags}>
                {(planType === MDVTypes.MEDICAL ||
                  planType === MDVTypes.DENTAL ||
                  planType === MDVTypes.VISION) &&
                  !isUndefined(getHRAContribution(contributionFiled)) &&
                  taggedHRAplan?.id !== undefined &&
                  hraPlanIds.includes(taggedHRAplan?.id) &&
                  displayHRAContributionValue()}
              </div>
            </div>
            <span className={styles.taxAdvantageBr}></span>
          </div>

          {(planType === MDVTypes.MEDICAL && plan.type) ||
          (benefitCarrier && benefitCarrier.providerSearchUrl) ||
          (planType === MDVTypes.MEDICAL &&
            hasPdlFile(plan?.benefitCarrier)) ? (
            <div
              className={`${styles.links} ${
                planType === MDVTypes.MEDICAL && styles.medicalPlanInfo
              }`}
            >
              {planType === MDVTypes.MEDICAL &&
                (plan.type ? (
                  <div
                    ref={planTypeInfoHeightRef}
                    className={styles.link}
                    style={
                      !isEditMode ? { height: planTypeInfoHeight } : undefined
                    }
                  >
                    <div className={styles.icon}>
                      <img src={iconPlayPlanInfo} alt="Plan Info" />
                    </div>
                    <div
                      className={styles.planTypeButton}
                      onClick={openPlanTypeDrawer}
                    >
                      {plan.hsaCompatible ? 'HSA' : plan.type}{' '}
                      {getText(INFO, language)}
                    </div>
                  </div>
                ) : (
                  <div
                    ref={planTypeInfoHeightRef}
                    className={styles.link}
                    style={
                      !isEditMode ? { height: planTypeInfoHeight } : undefined
                    }
                  ></div>
                ))}
              {benefitCarrier && benefitCarrier.providerSearchUrl ? (
                <div
                  ref={providerSearchHeightRef}
                  style={
                    !isEditMode ? { height: providerSearchHeight } : undefined
                  }
                  className={styles.link}
                >
                  <div className={styles.icon}>
                    <img src={iconDocSearch} alt="doctor search" />
                  </div>
                  <div
                    className={styles.providerSearch}
                    onClick={openProviderSearch}
                  >
                    {getText(PROVIDER_SEARCH, language)}
                  </div>
                </div>
              ) : (
                <div
                  style={
                    !isEditMode ? { height: providerSearchHeight } : undefined
                  }
                  className={styles.link}
                ></div>
              )}
              {planType === MDVTypes.MEDICAL &&
              hasPdlFile(plan?.benefitCarrier) ? (
                <div
                  ref={prescriptionDrugListRef}
                  style={
                    !isEditMode
                      ? { height: prescriptionDrugListHeight }
                      : undefined
                  }
                  className={styles.link}
                >
                  <div className={styles.icon}>
                    <img src={PrescriptionPill} alt="prescription pill" />
                  </div>
                  <div
                    className={styles.providerSearch}
                    onClick={openPrescriptionDrugList}
                  >
                    Prescription Drug List
                  </div>
                </div>
              ) : (
                <div
                  style={
                    !isEditMode
                      ? { height: prescriptionDrugListHeight }
                      : undefined
                  }
                  className={styles.link}
                ></div>
              )}
            </div>
          ) : (
            attachedPlans?.some(
              (item) =>
                (planType === MDVTypes.MEDICAL && item.type) ||
                (item?.benefitCarrier &&
                  item?.benefitCarrier?.providerSearchUrl) ||
                (planType === MDVTypes.MEDICAL &&
                  hasPdlFile(item?.benefitCarrier))
            ) && (
              <div
                className={`${styles.links} ${
                  planType === MDVTypes.MEDICAL && styles.medicalPlanInfo
                }`}
                style={
                  !isEditMode
                    ? {
                        height:
                          (providerSearchHeight || 0) +
                          (planTypeInfoHeight || 0) +
                          (prescriptionDrugListHeight || 0) +
                          12,
                      }
                    : undefined
                }
              ></div>
            )
          )}
        </div>
      );
    };

    const getServiceText = useCallback(
      (
        benefitCode: number,
        customServices: any[],
        fieldFormatter: string,
        title?: any
      ): any => {
        const filteredCustomService = customServices.find(
          (service: any) => service.benefitCode.code === benefitCode
        );

        const networkTypeForLabel =
          selectedNetwork === IN_NETWORK.value ? 'IN' : ('OUT' as 'IN' | 'OUT');

        const isInvalidDisplayValue = (val: any) => {
          const invalidValues = ['-', '--', '---', 'NO DATA', ''];
          return (
            val === undefined ||
            val === null ||
            invalidValues.includes(String(val).toUpperCase())
          );
        };

        const FREQUENCY_CODES = [102, 103, 104];
        const frequencyTitles = [
          'VISION_FREQUENCY_Eye Exam Frequency',
          'VISION_FREQUENCY_Lenses Frequency',
          'VISION_FREQUENCY_Frames Frequency',
        ];
        const isFrequencyService = FREQUENCY_CODES.includes(benefitCode);
        const isFrequencyApplicable = frequencyTitles.some(
          (title) => !notApplicableTitles.includes(title)
        );

        let label = '';
        let translatedLabel = '';
        let updatedNetwork = { info: defaultValue };

        let fallback: any = null;
        if (planType === MDVTypes.VISION && isFrequencyService) {
          fallback = VISION_FREQUENCY_LIST.find(
            (item) => item.serviceBenefitCode === benefitCode
          );
        } else if (planType === MDVTypes.VISION) {
          fallback = VISION_SERVICES_LIST.find(
            (item) => item.serviceBenefitCode === benefitCode
          );
        } else if (planType === MDVTypes.DENTAL) {
          fallback = DENTAL_SERVICES_LIST.find(
            (item) => item.serviceBenefitCode === benefitCode
          );
        } else if (planType === MDVTypes.MEDICAL) {
          fallback = MEDICAL_SERVICES_LIST.find(
            (item) => item.serviceBenefitCode === benefitCode
          );
        }

        // Helper function to get consistent field name for height synchronization
        // This matches the logic used in MDVCardTitles getTextByField
        // For services, we need to use the translated field name from FIELD_KEYS
        // to match what MDVCardTitles uses for the heightSyncKey
        const getConsistentFieldName = (serviceName: string) => {
          // If we have a title with fieldName, use the translated version from FIELD_KEYS
          // This ensures the label matches what MDVCardTitles uses
          if (title?.fieldName) {
            const translatedFieldName = getText(
              FIELD_KEYS[title.fieldName as keyof typeof FIELD_KEYS],
              language
            );
            if (translatedFieldName) {
              return translatedFieldName;
            }
          }
          return serviceName || fallback?.shortName || '';
        };

        if (filteredCustomService) {
          label = filteredCustomService?.benefitCode?.shortName?.trim();
          if (language == 'SPANISH') {
            translatedLabel =
              filteredCustomService?.benefitCode?.translations?.SPANISH?.shortName?.trim();
          }

          const alreadyPushed =
            filteredServicesRef.current.some(
              (svc: any) =>
                svc.benefitCode?.code ===
                filteredCustomService.benefitCode?.code
            ) ||
            frequencyServicesRef.current.some(
              (svc: any) =>
                svc.benefitCode?.code ===
                filteredCustomService.benefitCode?.code
            );

          if (!alreadyPushed) {
            if (
              planType === MDVTypes.VISION &&
              isFrequencyService &&
              isFrequencyApplicable
            ) {
              frequencyServicesRef.current.push(filteredCustomService);
            } else {
              filteredServicesRef.current.push(filteredCustomService);
            }
          }

          const network = get(
            filteredCustomService,
            `serviceValue.${networkType}Network`
          );
          updatedNetwork = {
            ...network,
            info: isInvalidDisplayValue(network?.info)
              ? defaultValue
              : network?.info,
          };
        } else if (!isEditMode && isMobile) {
          // Fallback mode for mobile view

          if (fallback) {
            label = fallback.shortName;

            const fallbackObj = {
              benefitCode: {
                code: benefitCode,
                shortName: fallback.shortName,
              },
              isFallback: true,
            };

            const alreadyPushed =
              filteredServicesRef.current.some(
                (svc: any) => svc.benefitCode?.code === benefitCode
              ) ||
              frequencyServicesRef.current.some(
                (svc: any) => svc.benefitCode?.code === benefitCode
              );

            if (!alreadyPushed) {
              if (
                planType === MDVTypes.VISION &&
                isFrequencyService &&
                isFrequencyApplicable
              ) {
                frequencyServicesRef.current.push(fallbackObj);
              } else {
                filteredServicesRef.current.push(fallbackObj);
              }
            }

            const isFirstFilteredService =
              filteredServicesRef.current[0]?.benefitCode?.code === benefitCode;

            const isFirstFrequencyService =
              frequencyServicesRef.current[0]?.benefitCode?.code ===
              benefitCode;

            const internationaliedLabel =
              getText(
                FIELD_KEYS[title?.fieldName as keyof typeof FIELD_KEYS],
                language
              ) ?? translatedLabel;

            const displayLabel =
              planType === MDVTypes.VISION && label?.endsWith('Frequency')
                ? label?.replace(/Frequency$/i, '').trim()
                : internationaliedLabel;

            return (
              <div>
                {isMobile &&
                  planType === MDVTypes.VISION &&
                  isFrequencyService &&
                  isFirstFrequencyService &&
                  isFrequencyApplicable && (
                    <div className={styles.mobileSectionHeader}>
                      {getText(FREQUENCIES_TEXT, language)}
                    </div>
                  )}
                {isMobile && isFirstFilteredService && (
                  <div className={styles.mobileSectionHeader}>
                    {getText(SERVICES_TEXT, language)}
                  </div>
                )}
                {isMobile && (
                  <div className={styles.serviceHeaderMobile}>
                    {displayLabel}
                  </div>
                )}
                <MDVFormattedValue
                  removeSup={planType === BENEFIT_TYPES.VISION}
                  data={{ info: defaultValue }}
                  formatter={fieldFormatter}
                  isEdit={isEditMode}
                  fieldName={getConsistentFieldName(translatedLabel || label)}
                  networkType={networkTypeForLabel}
                  labelParams={plan?.planLabels?.parameters}
                  dataIndex={dataIndex}
                  isMobile={isMobile}
                />
              </div>
            );
          }

          // fallback not found
          return null;
        } else {
          const filteredCustomServicesOfAllPlans = attachedPlans
            .map((plan) =>
              plan?.customServices?.find(
                (service: any) => service.benefitCode.code === benefitCode
              )
            )
            .filter((item) => item != undefined && item != null);
          if (filteredCustomServicesOfAllPlans.length > 0) {
            const serviceBenefitCode =
              filteredCustomServicesOfAllPlans[0].benefitCode;
            label = serviceBenefitCode?.shortName?.trim();
            if (language == 'SPANISH') {
              translatedLabel =
                serviceBenefitCode?.translations?.SPANISH?.shortName?.trim();
            }
          }

          // Not in edit mode and fallback not allowed
          return isEditMode ? (
            <MissingInfo
              style={
                !isMobile
                  ? {
                      height: getMaxHeight(
                        getConsistentFieldName(translatedLabel || label) || ''
                      ),
                    }
                  : {}
              }
              missingInfoText="Missing Info"
            />
          ) : isMobile ? null : (
            <div
              style={
                !isMobile
                  ? {
                      height: getMaxHeight(
                        getConsistentFieldName(translatedLabel || label) || ''
                      ),
                    }
                  : {}
              }
              className={styles.emptyValueServices}
            >
              {defaultValue}
            </div>
          );
        }

        const isFirstFilteredService =
          filteredServicesRef.current[0]?.benefitCode?.code === benefitCode;

        const isFirstFrequencyService =
          frequencyServicesRef.current[0]?.benefitCode?.code === benefitCode;

        const internationaliedLabel =
          getText(
            FIELD_KEYS[title?.fieldName as keyof typeof FIELD_KEYS],
            language
          ) ?? (translatedLabel ? translatedLabel : label);

        const displayLabel =
          planType === MDVTypes.VISION && label?.endsWith('Frequency')
            ? label?.replace(/Frequency$/i, '').trim()
            : internationaliedLabel;
        return (
          <div>
            {isMobile &&
              planType === MDVTypes.VISION &&
              isFrequencyService &&
              isFirstFrequencyService &&
              isFrequencyApplicable && (
                <div className={styles.mobileSectionHeader}>
                  {getText(FREQUENCIES_TEXT, language)}
                </div>
              )}
            {isMobile && isFirstFilteredService && (
              <div className={styles.mobileSectionHeader}>
                {getText(SERVICES_TEXT, language)}
              </div>
            )}
            {isMobile && (
              <div className={styles.serviceHeaderMobile}>{displayLabel}</div>
            )}
            <MDVFormattedValue
              removeSup={planType === BENEFIT_TYPES.VISION}
              data={updatedNetwork}
              formatter={fieldFormatter}
              isEdit={isEditMode}
              fieldName={getConsistentFieldName(translatedLabel || label)}
              networkType={networkTypeForLabel}
              labelParams={plan?.planLabels?.parameters}
              dataIndex={dataIndex}
              isMobile={isMobile}
            />
          </div>
        );
      },
      [
        attachedPlans,
        language,
        isEditMode,
        isMobile,
        networkType,
        plan?.planLabels?.parameters,
        planType,
        selectedNetwork,
        notApplicableTitles,
        dataIndex,
        getMaxHeight,
      ]
    );

    const getRxText = useCallback(
      (
        rxCode: string,
        rxCosts: any,
        fieldFormatter: string,
        rxType: string
      ) => {
        if (rxType === 'rxCost') {
          const { inNetwork = {}, outOfNetwork = {} } = rxCosts;
          const network =
            selectedNetwork === IN_NETWORK.value ? inNetwork : outOfNetwork;

          const networkType =
            selectedNetwork === IN_NETWORK.value
              ? 'IN'
              : ('OUT' as 'IN' | 'OUT');

          const rxTierTitlesToExclude = notApplicableTitles
            .filter((title) => title.startsWith('MEDICAL_RX_'))
            .map((title) => title.replace('MEDICAL_RX_', ''));

          const filteredRxTierLabels = RX_TIER_LABELS.filter(
            (tier) => !rxTierTitlesToExclude.includes(tier.label)
          );

          const rxTierKey =
            RX_TIER_FIELD_KEYS.rxCost?.[
              rxCode as keyof typeof RX_TIER_FIELD_KEYS.rxCost
            ];
          const label = rxTierKey
            ? getText(rxTierKey, language)
            : RX_TIER_LABELS.find((tier) => tier.value === rxCode)?.label;

          const rxDeductiblesMissing = [
            'MEDICAL_RX_Individual Deductible',
            'MEDICAL_RX_Family Deductible',
            'MEDICAL_RX_Individual Out-of-Pocket Max',
            'MEDICAL_RX_Family Out-of-Pocket Max',
          ].every((title) => notApplicableTitles.includes(title));

          const isFirstMatchRetailTier =
            filteredRxTierLabels.findIndex((tier) => tier.value === rxCode) ===
            0;

          const shouldShowRxHeader =
            isMobile &&
            !isEditMode &&
            isFirstMatchRetailTier &&
            rxDeductiblesMissing;

          const showFallback =
            !network[rxCode]?.costSharingPolicy &&
            !network[rxCode]?.benefitCovered;

          return (
            <div>
              {shouldShowRxHeader && (
                <div className={styles.mobileSectionHeader}>
                  {getText(RX, language)}
                </div>
              )}
              {isMobile && (
                <div className={styles.serviceHeaderMobile}>{label}</div>
              )}
              {showFallback ? (
                isEditMode ? (
                  <MissingInfo
                    style={
                      !isMobile ? { height: getMaxHeight(label || '') } : {}
                    }
                    missingInfoText="Missing Info"
                  />
                ) : (
                  <div
                    style={
                      !isMobile ? { height: getMaxHeight(label || '') } : {}
                    }
                    className={styles.emptyValueServices}
                  >
                    {defaultValue}
                  </div>
                )
              ) : (
                <MDVFormattedValue
                  removeSup={planType === BENEFIT_TYPES.VISION}
                  data={network[rxCode]}
                  formatter={fieldFormatter}
                  isEdit={isEditMode}
                  fieldName={label!}
                  networkType={networkType}
                  labelParams={plan?.planLabels?.parameters}
                  dataIndex={dataIndex}
                  isMobile={isMobile}
                  isRx
                />
              )}
            </div>
          );
        } else if (rxType === 'mailRxCost') {
          const { inNetwork = {}, outOfNetwork = {} } = mailOrderRxCosts;
          const network =
            selectedNetwork === IN_NETWORK.value ? inNetwork : outOfNetwork;

          const networkType =
            selectedNetwork === IN_NETWORK.value
              ? 'IN'
              : ('OUT' as 'IN' | 'OUT');
          const mailOrderRxTierTitlesToExclude = notApplicableTitles
            .filter((title) => title.startsWith('MEDICAL_RX_Mail'))
            .map((title) => title.replace('MEDICAL_RX_', ''));

          const filteredMailOrderRxTierLabels =
            MAIL_ORDER_RX_TIER_LABELS.filter(
              (tier) => !mailOrderRxTierTitlesToExclude.includes(tier.label)
            );

          const rxTierKey =
            RX_TIER_FIELD_KEYS.mailRxCost?.[
              rxCode as keyof typeof RX_TIER_FIELD_KEYS.mailRxCost
            ];
          const label = rxTierKey
            ? getText(rxTierKey, language)
            : MAIL_ORDER_RX_TIER_LABELS.find((tier) => tier.value === rxCode)
                ?.label;

          const rxDeductibleAndRetailTierMissing = [
            'MEDICAL_RX_Individual Deductible',
            'MEDICAL_RX_Family Deductible',
            'MEDICAL_RX_Individual Out-of-Pocket Max',
            'MEDICAL_RX_Family Out-of-Pocket Max',
            'MEDICAL_RX_Retail Tier 1',
            'MEDICAL_RX_Retail Tier 2',
            'MEDICAL_RX_Retail Tier 3',
            'MEDICAL_RX_Retail Tier 4',
            'MEDICAL_RX_Retail Tier 5',
            'MEDICAL_RX_Retail Tier 6',
            'MEDICAL_RX_Retail Tier 7',
            'MEDICAL_RX_Retail Tier 8',
          ].every((title) => notApplicableTitles.includes(title));

          const isFirstMatchMailOrderRetailTier =
            filteredMailOrderRxTierLabels.findIndex(
              (tier) => tier.value === rxCode
            ) === 0;

          const shouldShowRxHeader =
            isMobile &&
            !isEditMode &&
            isFirstMatchMailOrderRetailTier &&
            rxDeductibleAndRetailTierMissing;

          const showFallback =
            !network[rxCode]?.costSharingPolicy &&
            !network[rxCode]?.benefitCovered;

          return (
            <div>
              {shouldShowRxHeader && (
                <div className={styles.mobileSectionHeader}>
                  {getText(RX, language)}
                </div>
              )}
              {isMobile && (
                <div className={styles.serviceHeaderMobile}>{label}</div>
              )}
              {showFallback ? (
                isEditMode ? (
                  <MissingInfo
                    style={
                      !isMobile ? { height: getMaxHeight(label || '') } : {}
                    }
                    missingInfoText="Missing Info"
                  />
                ) : (
                  <div
                    style={
                      !isMobile ? { height: getMaxHeight(label || '') } : {}
                    }
                    className={styles.emptyValueServices}
                  >
                    {defaultValue}
                  </div>
                )
              ) : (
                <MDVFormattedValue
                  removeSup={planType === BENEFIT_TYPES.VISION}
                  data={network[rxCode]}
                  isEdit={isEditMode}
                  formatter={fieldFormatter}
                  fieldName={label!}
                  networkType={networkType}
                  labelParams={plan?.planLabels?.parameters}
                  dataIndex={dataIndex}
                  isMobile={isMobile}
                  isRx
                />
              )}
            </div>
          );
        }
      },
      [
        selectedNetwork,
        isEditMode,
        isMobile,
        planType,
        plan?.planLabels?.parameters,
        mailOrderRxCosts,
        notApplicableTitles,
        dataIndex,
        getMaxHeight,
        language,
      ]
    );

    const showAdditionalServices = () => {
      const invalidValues = [defaultValue, DOUBLE_DASH, TRIPLE_DASH, NO_DATA];
      const rows = [] as any;
      if (!isEditMode) {
        additionalServices
          .filter((service) => {
            if (!benguide.isAdvanceProfile) {
              return true;
            }
            if (planType === MDVTypes.MEDICAL) {
              return !MEDICAL_ADVANCED_PROFILE_PARAMETERS.includes(
                service.fieldValue
              );
            }
            if (planType === MDVTypes.VISION) {
              return !VISION_ADVANCED_PROFILE_PARAMETERS.includes(
                service.fieldValue
              );
            }
            return true;
          })
          .filter((service) => !!service.fieldValue)
          .filter(
            (service) =>
              !invalidValues.includes(String(service.fieldValue).trim())
          )
          .forEach((service, key) => {
            const { serviceBenefitCode, fieldFormatter = '' } = service;

            rows.push(
              <div key={key} className={getRowWrapperClass(true)}>
                {getServiceText(
                  serviceBenefitCode,
                  customServices,
                  fieldFormatter
                )}
              </div>
            );
          });
      }
      if (isEditMode) {
        additionalServices
          .filter((service) => {
            if (!benguide.isAdvanceProfile) {
              return true;
            }
            if (planType === MDVTypes.MEDICAL) {
              return !MEDICAL_ADVANCED_PROFILE_PARAMETERS.includes(
                service.fieldValue
              );
            }
            if (planType === MDVTypes.VISION) {
              return !VISION_ADVANCED_PROFILE_PARAMETERS.includes(
                service.fieldValue
              );
            }
            return true;
          })
          .forEach((service, key) => {
            const { serviceBenefitCode, fieldFormatter = '' } = service;
            rows.push(
              <div key={key} className={getRowWrapperClass(true)}>
                {getServiceText(
                  serviceBenefitCode,
                  customServices,
                  fieldFormatter
                )}
              </div>
            );
          });
      }

      return rows;
    };

    const getEditCustomFieldModal = (
      isModelOpen: boolean,
      closeModal: () => void,
      customField: CustomField
    ) => {
      const handleOnSave = async (customField: CustomField) => {
        if (planType == MDVTypes.MEDICAL) {
          await dispatch(editCustomFieldValue(customField));
          await dispatch(getCustomFieldsToState());
        } else if (planType == MDVTypes.DENTAL) {
          await dispatch(editDentalCustomFieldValue(customField));
          await dispatch(getDentalCustomFieldsToState());
        } else if (planType == MDVTypes.VISION) {
          await dispatch(editVisionCustomFieldValue(customField));
          await dispatch(getVisionCustomFieldsToState());
        }

        closeModal();
      };
      return (
        <EditCustomRowModal
          visible={isModelOpen}
          onClose={closeModal}
          onSave={handleOnSave}
          customFieldData={customField}
        />
      );
    };
    const upClient = useUPClient();

    return (
      <div
        className={styles.medicalCardValuesWrapper}
        data-edit-mode={isEditMode.toString()}
      >
        {attachedPlans.length > 1 && (
          <div className={styles.moveWrapper}>
            <EditableHotspot
              alwaysVisible={false}
              customModal={() => <></>}
              overlayComponent={
                <div className={styles.moveOverlay}>
                  {!hideLeftMoveButton && (
                    <div className={styles.left} onClick={moveLeft}>
                      <img src={iconLeftArrow} alt="move-left-icon" />
                      <label>Move</label>
                    </div>
                  )}
                  {!hideRightMoveButton && (
                    <div className={styles.right} onClick={moveRight}>
                      <label>Move</label>
                      <img src={iconRightArrow} alt="move-right-icon" />
                    </div>
                  )}
                </div>
              }
            >
              <div className={styles.moveArea}></div>
            </EditableHotspot>
          </div>
        )}

        <div
          className={
            selectedPlanForCompare.includes(plan.id)
              ? styles.selectedCardShadow
              : styles.topShadow
          }
        ></div>
        {dataGroups.map((titleGroup, titleGroupIndex) => {
          const { event } = titleGroup;
          if (titleGroup.isSeparateRowEvent) {
            return titleGroup?.elements?.map((title: any) => {
              if (title.fieldName === CUSTOM_FIELD) {
                return plan?.customFields?.length === 0 ? (
                  <>
                    {isEditMode ? (
                      <div className={styles.emptyCustom}></div>
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  plan?.customFields?.map(
                    (customField: CustomField, index: number) => {
                      if (
                        !isEditMode &&
                        notApplicableTitles
                          .filter((title) => title !== 'UnavailablePlanNetwork')
                          ?.includes(customField?.fieldName)
                      ) {
                        return null;
                      }
                      return (
                        <div key={index} className={getRowWrapperClass(true)}>
                          {isEditMode ? (
                            <EditableHotspot
                              key={index}
                              alwaysVisible={false}
                              editIcon={<img src={iconEdit} alt="edit" />}
                              editText="Edit"
                              customModal={(
                                isModalVisible: boolean,
                                closeModal: () => void
                              ) =>
                                getEditCustomFieldModal(
                                  isModalVisible,
                                  closeModal,
                                  customField
                                )
                              }
                              editIconWrapperClassName={styles.singleRowEdit}
                            >
                              <MDVCardCustomValue
                                title={title}
                                data={customField}
                                isMobile={isMobile}
                                isEditMode={isEditMode}
                                dataIndex={dataIndex}
                              />
                            </EditableHotspot>
                          ) : (
                            <MDVCardCustomValue
                              title={title}
                              data={customField}
                              isMobile={isMobile}
                              isEditMode={isEditMode}
                              dataIndex={dataIndex}
                            />
                          )}
                        </div>
                      );
                    }
                  )
                );
              }
              if (title.fieldName === 'spaceField' && !isMobile) {
                return <div className={styles.titleSeperator} />;
              }
            });
          }
          return (
            <EditableHotspot
              key={titleGroupIndex}
              alwaysVisible={false}
              editIcon={<img src={iconEdit} alt="edit" />}
              editText="Edit"
              useChannel={{
                client: upClient,
                channel: getMDVChannel(),
                event: event,
                data: { ...plan, planIds: planIds },
                dbgMetaData: benguide,
              }}
              dataTestId={`${titleGroup.event}-plan${dataIndex}`}
            >
              {titleGroup.elements.map((title: any, titleIndex: number) => {
                if (showRxRow(title, attachedPlans, selectedNetwork)) {
                  if (
                    !(
                      nonExistCustomServices &&
                      nonExistCustomServices.hasOwnProperty(
                        title.serviceBenefitCode
                      ) &&
                      nonExistCustomServices[title.serviceBenefitCode] ===
                        attachedPlans.length
                    ) &&
                    (!(
                      nonExistRxCosts &&
                      nonExistRxCosts.hasOwnProperty(
                        title.fieldName + title.rxCode
                      ) &&
                      nonExistRxCosts[title.fieldName + title.rxCode] ===
                        attachedPlans.length
                    ) ||
                      (title.rxCode && isEditMode))
                  ) {
                    // Only show services with showTitleIfExists when in advanced profile. Those are the additional services defined
                    if (
                      title.serviceBenefitCode &&
                      title.showTitleIfExists &&
                      !currentBenguide.isAdvanceProfile
                    ) {
                      return null;
                    }
                    if (!title.fieldName) {
                      return <>{getPlanValues(title)}</>;
                    } else {
                      const planValueContent = getPlanValues(title);

                      // Determine if this row has actual benefit content
                      // Check if it's a separator (contains headerSeperator)
                      const isSeparator =
                        title.fieldValue &&
                        hideHeader(
                          planType,
                          notApplicableTitles,
                          title.fieldValue,
                          selectedNetwork === 'IN_NETWORK' ? 'IN' : 'OUT'
                        );

                      // Only count rows with actual benefit data as content
                      // These are rows with specific fieldNames that contain actual benefit details
                      // Exclude separators, space fields, and null content
                      const hasActualContent =
                        !isSeparator &&
                        planValueContent !== null &&
                        // Check for specific meaningful field types
                        ((title.fieldName &&
                          title.fieldName !== 'spaceField' &&
                          (title.fieldName === 'deductibleCost' ||
                            title.fieldName === 'outOfPocketMax' ||
                            title.fieldName === 'monthlyCost' ||
                            title.fieldName === 'planNetwork' ||
                            title.fieldName === 'indWithinFamilyDeductible' ||
                            title.fieldName === 'indWithinFamilyOOPMax' ||
                            title.fieldName === 'rxDeductibles' ||
                            title.fieldName === 'rxOOP' ||
                            title.fieldName === 'calendarYearMax' ||
                            title.fieldName === 'orthodontiaLifetimeMax' ||
                            title.fieldName === 'customFields')) ||
                          title.serviceBenefitCode || // Service rows
                          title.rxCode); // RX rows

                      return (
                        <div
                          key={titleIndex}
                          className={getRowWrapperClass(hasActualContent)}
                        >
                          {planValueContent}
                        </div>
                      );
                    }
                  }
                  return null;
                }
              })}
            </EditableHotspot>
          );
        })}

        {summarySection}
        {planType === MDVTypes.MEDICAL && (
          <PlanTypeInfoDrawer
            isDrawerOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            isHSA={plan.hsaCompatible}
            planType={plan.type}
          />
        )}
        <ProviderSearchModal
          visible={isOpenProviderSearch}
          onCancel={() => setIsOpenProviderSearch(false)}
          carrier={plan.benefitCarrier}
          planName={plan.name}
          networkName={plan.planNetworkName}
        />
        <PrescriptionDrugListModal
          visible={isOpenPrescriptionDrugList}
          onCancel={() => setIsOpenPrescriptionDrugList(false)}
          carrier={plan.benefitCarrier}
          planName={plan.name}
          networkName={plan.planNetworkName}
        />
      </div>
    );
  }
);

MDVCardValues.displayName = 'MDVCardValues';
export default MDVCardValues;
